import {
  isRelayablyPlanComingSoon,
  RELAYABLY_COUNTRY_OPTIONS,
  RELAYABLY_COUNTRY_LABELS,
  RELAYABLY_DEFAULT_COUNTRY,
  type RelayablyCountryCode,
} from '@monorepo/relayably/config'
import { Theme } from '@monorepo/relayably/theme'
import {
  RelayablyPhoneComplianceProfile,
  RelayablyCalendarProvider,
  RelayablyVoiceOption,
  RelayablyWorkingDay,
  RelayablyWorkingHoursEntry,
  RelayablyWorkspaceDocument,
  UsageType,
} from '@monorepo/relayably/workspaces'
import { Check } from 'phosphor-react-native'
import { Audio, AVPlaybackStatus, InterruptionModeAndroid, InterruptionModeIOS } from 'expo-av'
import * as DocumentPicker from 'expo-document-picker'
import * as FileSystem from 'expo-file-system'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Linking, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native'
import SelectDropdown from 'react-native-select-dropdown'
import { applySession, logout } from '../auth/authSlice'
import { phoneForwardingGuideStorage } from '../dashboard/phoneForwardingGuideStorage'
import { useAppDispatch, useAppSelector } from '../store'
import { OnboardingLayout } from './flow/components/OnboardingLayout'
import { PhoneNumberStep } from './flow/components/PhoneNumberStep'
import { onboardingCheckoutStorage, PendingComplianceUpload } from './onboardingCheckoutStorage'
import { relayablyPhoneNumberApi } from './relayablyPhoneNumberApi'
import { relayablyWorkspaceApi } from './relayablyWorkspaceApi'
import { RelayablyPaymentProduct } from './types'
import { bootstrapWorkspace, replaceWorkspace } from './workspaceSlice'

type CountryCode = RelayablyCountryCode
type NumberKind = 'mobile' | 'local' | 'tollfree'
type StepKey = 'plan' | 'business' | 'setup' | 'compliance' | 'voice' | 'calendar' | 'done'
type VoiceGenderFilter = 'any' | 'female' | 'male'
type VoiceAgeFilter = 'any' | 'young' | 'middle_aged' | 'old'
type ComplianceRequirementsState = Awaited<ReturnType<typeof relayablyPhoneNumberApi.getComplianceRequirements>>
type PendingComplianceUploadMap = Record<string, PendingComplianceUpload>

type StepDefinition = {
  accentLabel: string
  atmosphere: {
    glowColors: [string, string, string]
    railAccent: string
    sideBackground: string
  }
  highlights: string[]
  key: StepKey
  subtitle: string
  title: string
}

type PlanDefinition = {
  allowedNumberKinds: NumberKind[]
  description: string
  displayPrice: string
  features: string[]
  keyAgents: string[]
  label: string
  outboundNote?: string
  interval: string
  value: UsageType
}

const timezonesByCountry: Record<CountryCode, string[]> = {
  AU: ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth'],
}

const workingDayOptions: Array<{ label: string; value: RelayablyWorkingDay }> = [
  { label: 'Mon', value: 'mon' },
  { label: 'Tue', value: 'tue' },
  { label: 'Wed', value: 'wed' },
  { label: 'Thu', value: 'thu' },
  { label: 'Fri', value: 'fri' },
  { label: 'Sat', value: 'sat' },
  { label: 'Sun', value: 'sun' },
]

const timeOptions = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0')
  const minutes = index % 2 === 0 ? '00' : '30'
  return `${hours}:${minutes}`
})

const mapPlanDefinitions = (products: RelayablyPaymentProduct[]): PlanDefinition[] =>
  products.map(product => ({
    allowedNumberKinds: product.allowedNumberKinds,
    description: product.description,
    displayPrice: product.displayPrice,
    features: product.features,
    interval: product.interval,
    keyAgents: product.keyAgents,
    label: product.name,
    outboundNote: product.note,
    value: product.id,
  }))

const countryLabelByCode = RELAYABLY_COUNTRY_LABELS
const countryCodeByLabel = Object.fromEntries(RELAYABLY_COUNTRY_OPTIONS.map(option => [option.label, option.code])) as Record<string, CountryCode>

const cloneWorkspace = (workspace: RelayablyWorkspaceDocument) => JSON.parse(JSON.stringify(workspace)) as RelayablyWorkspaceDocument

const readUriAsBase64 = async (uri: string) => {
  if (Platform.OS === 'web') {
    const response = await fetch(uri)
    const blob = await response.blob()

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  return FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  })
}

const numberKindLabel: Record<NumberKind, string> = {
  local: 'Local',
  mobile: 'Mobile',
  tollfree: 'Toll Free',
}

const providerLabel: Record<RelayablyCalendarProvider, string> = {
  calendly: 'Calendly',
  'cal.com': 'Cal.com',
}

const voiceGenderFilters: Array<{ label: string; value: VoiceGenderFilter }> = [
  { label: 'Any', value: 'any' },
  { label: 'Female', value: 'female' },
  { label: 'Male', value: 'male' },
]

const voiceAgeFilters: Array<{ label: string; value: VoiceAgeFilter }> = [
  { label: 'Any', value: 'any' },
  { label: 'Young', value: 'young' },
  { label: 'Middle Aged', value: 'middle_aged' },
  { label: 'Older', value: 'old' },
]

const complianceBusinessIdentityOptions = [
  { label: 'Direct Customer', value: 'DIRECT_CUSTOMER' },
  { label: 'ISV', value: 'INDEPENDENT_SOFTWARE_VENDOR' },
]

const complianceRegistrationAuthorityOptions = [
  { label: 'UK:CRN', value: 'UK:CRN' },
  { label: 'US:EIN', value: 'US:EIN' },
  { label: 'CA:CBN', value: 'CA:CBN' },
  { label: 'AU:ACN', value: 'AU:ACN' },
  { label: 'Other', value: 'OTHER' },
]

const COMPLIANCE_DOCUMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
]

const COMPLIANCE_DOCUMENT_TYPE_LABEL = 'PDF, PNG, JPG, WEBP'

const atmosphere = (glowColors: [string, string, string], railAccent: string, sideBackground: string) => ({
  glowColors,
  railAccent,
  sideBackground,
})

const getStepDefinitions = (selectedPlan: UsageType, includeCalendar: boolean, includeCompliance: boolean): StepDefinition[] => [
  {
    accentLabel: 'Choose a plan',
    atmosphere: atmosphere(['rgba(184, 132, 255, 0.22)', 'rgba(255, 101, 178, 0.08)', 'transparent'], '#b884ff', '#100613'),
    highlights: ['Pick the plan that fits you best.', 'Your plan decides which number types and extra options you can use.'],
    key: 'plan',
    title: 'Choose A Plan',
    subtitle: 'Start with the plan that fits the way you work.',
  },
  {
    accentLabel: selectedPlan === 'personal' ? 'Instructions and notes' : 'Context and knowledge',
    atmosphere: atmosphere(['rgba(127, 167, 255, 0.2)', 'rgba(87, 209, 255, 0.08)', 'transparent'], '#79a9ff', '#071019'),
    highlights:
      selectedPlan === 'personal'
        ? ['Tell Relayably which calls matter to you and which ones should be shut down quickly.', 'These notes become the first instructions for your setup.']
        : ['Add your business name, website, and a short description so Relayably starts with the right knowledge.', 'You can write it yourself or let Relayably draft a first version for you.'],
    key: 'business',
    title: selectedPlan === 'personal' ? 'Instructions And Notes' : 'Context And Knowledge',
    subtitle:
      selectedPlan === 'personal'
        ? 'Add the first instructions you want Relayably to follow.'
        : 'Add the first knowledge Relayably should use during setup.',
  },
  {
    accentLabel: 'Choose your number',
    atmosphere: atmosphere(['rgba(66, 218, 188, 0.22)', 'rgba(62, 144, 255, 0.08)', 'transparent'], '#52d6be', '#041211'),
    highlights: ['Choose the Australian number you want to use.', 'We keep your selected number against the approved AU holding bundle while your compliance is under review.'],
    key: 'setup',
    title: 'Choose Your Number',
    subtitle: 'Pick the Australian number you want Relayably to use.',
  },
  ...(includeCompliance
    ? [
        {
          accentLabel: 'Phone compliance',
          atmosphere: atmosphere(['rgba(255, 171, 95, 0.22)', 'rgba(255, 213, 120, 0.08)', 'transparent'], '#ffbb6a', '#130b05'),
          highlights: ['Australian number compliance can require identity or address proof.', 'Relayably keeps the selected number on the approved AU holding bundle until your own bundle is approved.'],
          key: 'compliance' as const,
          title: 'Phone Compliance',
          subtitle: 'Add the details Twilio needs for this number type.',
        },
      ]
    : []),
  {
    accentLabel: 'Choose a voice',
    atmosphere: atmosphere(['rgba(140, 188, 255, 0.22)', 'rgba(126, 117, 255, 0.08)', 'transparent'], '#8cbcff', '#07101a'),
    highlights: ['Choose the voice Relayably should use when it answers.', 'Filter by accent, gender, and voice age to find the right fit.'],
    key: 'voice',
    title: 'Choose A Voice',
    subtitle: 'Pick the voice people will hear when Relayably answers.',
  },
  ...(includeCalendar
    ? [
        {
          accentLabel: 'Add a calendar',
          atmosphere: atmosphere(['rgba(255, 198, 115, 0.2)', 'rgba(255, 143, 92, 0.08)', 'transparent'], '#ffc673', '#120a05'),
          highlights: ['You can connect Calendly or Cal.com if you want call booking.', 'Add your API key, timezone, and working hours.'],
          key: 'calendar' as const,
          title: 'Add A Calendar',
          subtitle: 'Optional. Add this if you want calls to turn into bookings.',
        },
      ]
    : []),
  {
    accentLabel: 'Review and pay',
    atmosphere: atmosphere(['rgba(113, 152, 255, 0.22)', 'rgba(111, 108, 255, 0.08)', 'transparent'], '#7198ff', '#060914'),
    highlights: [
      'Review your selections, then continue to payment.',
      'After payment Relayably will set up the workspace, reserve the number, and then show the redirect information.',
    ],
    key: 'done',
    title: 'Review And Pay',
    subtitle: 'Check everything, pay the subscription, and let Relayably finish setup.',
  },
]

const getDefaultTimezone = (country: CountryCode) => timezonesByCountry[country][0] ?? 'UTC'

const toProperCase = (value: string) =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ')

const toCamelCase = (value: string) => value.replace(/_([a-z])/g, (_match, token: string) => token.toUpperCase())

const getComplianceFieldValue = (profile: RelayablyPhoneComplianceProfile | null, field: string) => {
  if (!profile) {
    return ''
  }

  if (field === 'address_sids') {
    return profile.addressSid
  }

  return String((profile.endUser as Record<string, unknown>)[toCamelCase(field)] ?? '')
}

const setComplianceFieldValue = (profile: RelayablyPhoneComplianceProfile, field: string, value: string | boolean) => {
  if (field === 'address_sids') {
    return profile
  }

  return {
    ...profile,
    endUser: {
      ...profile.endUser,
      [toCamelCase(field)]: value,
    },
  }
}

const getLocalComplianceMissingFields = (
  requirements: ComplianceRequirementsState | null,
  profile: RelayablyPhoneComplianceProfile | null
) => {
  if (!requirements || !profile) {
    return []
  }

  const requiredFields = Array.from(
    new Set([
      ...requirements.fields.map(field => field.key),
      ...requirements.requiredDocuments.flatMap(requirement =>
        requirement.acceptedDocuments.flatMap(document => document.fields)
      ),
    ])
  ).map(field => ({
    key: field,
    label: toProperCase(field.replace(/_/g, ' ')),
  }))

  return requiredFields
    .filter(field => {
      if (field.key === 'address_sids') {
        return !profile.address.customerName.trim() || !profile.address.street.trim() || !profile.address.city.trim() || !profile.address.region.trim() || !profile.address.postalCode.trim()
      }

      return !String(getComplianceFieldValue(profile, field.key)).trim()
    })
    .map(field => field.label)
}

const getLocalComplianceMissingDocuments = (
  requirements: ComplianceRequirementsState | null,
  profile: RelayablyPhoneComplianceProfile | null,
  pendingUploads: PendingComplianceUploadMap
) => {
  if (!requirements || !profile) {
    return []
  }

  return requirements.requiredDocuments
    .filter(
      requirement =>
        !pendingUploads[requirement.requirementName] &&
        !requirement.acceptedDocuments.some(document => {
          if (!document.type.trim()) {
            return false
          }

          if (document.fields.length === 0) {
            return true
          }

          return document.fields.every(field => {
            if (field === 'address_sids') {
              return (
                Boolean(profile.address.customerName.trim()) &&
                Boolean(profile.address.street.trim()) &&
                Boolean(profile.address.city.trim()) &&
                Boolean(profile.address.region.trim()) &&
                Boolean(profile.address.postalCode.trim())
              )
            }

            return Boolean(String(getComplianceFieldValue(profile, field)).trim())
          })
        })
    )
    .map(requirement => requirement.label)
}

const splitName = (fullName: string) => {
  const tokens = fullName.trim().split(/\s+/).filter(Boolean)
  return {
    firstName: tokens[0] ?? '',
    lastName: tokens.slice(1).join(' '),
  }
}

const getDefaultBusinessRegistrationIdentifier = (_country: CountryCode) => 'AU:ACN'

const matchesVoiceFilters = ({
  voice,
  accent,
  gender,
  age,
}: {
  voice: RelayablyVoiceOption
  accent: string
  gender: VoiceGenderFilter
  age: VoiceAgeFilter
}) => {
  if (gender !== 'any' && voice.gender !== gender) {
    return false
  }

  if (age !== 'any' && voice.age !== age) {
    return false
  }

  if (accent && voice.accent !== accent.toLowerCase()) {
    return false
  }

  return true
}

const defaultWorkingSchedule: RelayablyWorkingHoursEntry[] = [
  { day: 'mon', closed: false, start: '09:00', end: '17:00' },
  { day: 'tue', closed: false, start: '09:00', end: '17:00' },
  { day: 'wed', closed: false, start: '09:00', end: '17:00' },
  { day: 'thu', closed: false, start: '09:00', end: '17:00' },
  { day: 'fri', closed: false, start: '09:00', end: '17:00' },
  { day: 'sat', closed: true, start: '09:00', end: '17:00' },
  { day: 'sun', closed: true, start: '09:00', end: '17:00' },
]

const buildWorkingSchedule = (
  workingHours?:
    | {
        days?: RelayablyWorkingDay[]
        start?: string
        end?: string
        schedule?: RelayablyWorkingHoursEntry[]
      }
    | null
) => {
  if (workingHours?.schedule?.length) {
    const scheduleByDay = new Map(workingHours.schedule.map(entry => [entry.day, entry]))
    return defaultWorkingSchedule.map(entry => scheduleByDay.get(entry.day) ?? entry)
  }

  const legacyDays = workingHours?.days?.length ? workingHours.days : ['mon', 'tue', 'wed', 'thu', 'fri']
  const legacyStart = workingHours?.start || '09:00'
  const legacyEnd = workingHours?.end || '17:00'

  return defaultWorkingSchedule.map(entry => ({
    day: entry.day,
    closed: legacyDays.includes(entry.day) ? false : true,
    start: legacyDays.includes(entry.day) ? legacyStart : entry.start,
    end: legacyDays.includes(entry.day) ? legacyEnd : entry.end,
  }))
}

const isValidWebsite = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return true
  }

  if (/\s/.test(trimmed)) {
    return false
  }

  try {
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const parsed = new URL(normalized)
    return parsed.hostname.includes('.') && !parsed.hostname.startsWith('.')
  } catch {
    return false
  }
}

const getInitialCalendarTimezone = (country: CountryCode, workspace?: RelayablyWorkspaceDocument | null) => {
  const savedTimezone = workspace?.workspace.calendarIntegration?.timezone || workspace?.profile.timezone || ''
  return timezonesByCountry[country].includes(savedTimezone) ? savedTimezone : getDefaultTimezone(country)
}

const getActiveVoiceFilterSummary = (input: {
  accent: string
  gender: VoiceGenderFilter
  age: VoiceAgeFilter
}) => {
  const tokens: string[] = []

  if (input.accent.trim()) {
    tokens.push(input.accent.trim())
  }

  if (input.gender !== 'any') {
    tokens.push(voiceGenderFilters.find(filter => filter.value === input.gender)?.label ?? input.gender)
  }

  if (input.age !== 'any') {
    tokens.push(voiceAgeFilters.find(filter => filter.value === input.age)?.label ?? input.age)
  }

  return tokens
}

const buildStructuredWorkspaceDraft = ({
  authUser,
  businessDescription,
  businessName,
  businessWebsite,
  calendarApiKey,
  calendarEnabled,
  calendarProvider,
  calendarTimezone,
  complianceProfile,
  country,
  interestedCallNotes,
  selectedNumber,
  selectedPlan,
  selectedVoice,
  unsubscribeCallNotes,
  workingSchedule,
  workspace,
}: {
  authUser: { email: string; fullName: string }
  businessDescription: string
  businessName: string
  businessWebsite: string
  calendarApiKey: string
  calendarEnabled: boolean
  calendarProvider: RelayablyCalendarProvider | null
  calendarTimezone: string
  complianceProfile: RelayablyPhoneComplianceProfile | null
  country: CountryCode
  interestedCallNotes: string
  selectedNumber: RelayablyWorkspaceDocument['workspace']['phoneNumber'] | null
  selectedPlan: UsageType
  selectedVoice: RelayablyVoiceOption | null
  unsubscribeCallNotes: string
  workingSchedule: RelayablyWorkingHoursEntry[]
  workspace: RelayablyWorkspaceDocument
}) => {
  const nextWorkspace = cloneWorkspace(workspace)
  const resolvedTimezone = calendarEnabled ? calendarTimezone : getInitialCalendarTimezone(country, workspace)

  nextWorkspace.profile.country = countryLabelByCode[country]
  nextWorkspace.profile.timezone = resolvedTimezone
  nextWorkspace.workspace.workspaceName = nextWorkspace.workspace.workspaceName?.trim() || `${authUser.fullName} Workspace`
  nextWorkspace.workspace.businessName = businessName.trim()
  nextWorkspace.workspace.website = businessWebsite.trim()
  nextWorkspace.workspace.businessDescription = businessDescription.trim()
  nextWorkspace.workspace.interestedCallNotes = interestedCallNotes.trim()
  nextWorkspace.workspace.unsubscribeCallNotes = unsubscribeCallNotes.trim()
  nextWorkspace.workspace.phoneNumber = selectedNumber
  nextWorkspace.workspace.voiceSelection = selectedVoice
  nextWorkspace.workspace.templates = []
  nextWorkspace.workspace.agentConfigs = []
  nextWorkspace.workspace.assistant = null
  if (complianceProfile) {
    nextWorkspace.workspace.phoneComplianceProfiles = [
      ...(nextWorkspace.workspace.phoneComplianceProfiles ?? []).filter(profile => profile.id !== complianceProfile.id),
      complianceProfile,
    ]
  }
  nextWorkspace.workspace.calendarIntegration = {
    enabled: calendarEnabled,
    provider: calendarProvider,
    apiKey: calendarEnabled ? calendarApiKey.trim() : '',
    timezone: calendarEnabled ? calendarTimezone : '',
    workingHours: {
      days: calendarEnabled ? workingSchedule.filter(entry => !entry.closed).map(entry => entry.day) : ['mon', 'tue', 'wed', 'thu', 'fri'],
      start: calendarEnabled ? workingSchedule.find(entry => !entry.closed)?.start ?? '09:00' : '09:00',
      end: calendarEnabled ? workingSchedule.find(entry => !entry.closed)?.end ?? '17:00' : '17:00',
      schedule: calendarEnabled ? workingSchedule : defaultWorkingSchedule,
    },
  }
  nextWorkspace.onboarding.mode = 'structured'
  nextWorkspace.onboarding.source = 'structured'
  nextWorkspace.workspace.usageType = selectedPlan
  nextWorkspace.workspace.primaryUseCase = selectedPlan === 'personal' ? 'protect_focus_time' : 'capture_sales_enquiries'
  nextWorkspace.workspace.useCaseFlags = {
    afterHoursAnswering: false,
    bookAppointments: false,
    captureSalesEnquiries: selectedPlan !== 'personal',
    handleCustomerSupport: selectedPlan !== 'personal',
    protectFocusTime: selectedPlan === 'personal',
    qualifyLeads: selectedPlan === 'business',
    screenPersonalCalls: true,
  }

  return nextWorkspace
}

const resetVoiceFilters = (
  setVoiceAccent: (value: string) => void,
  setVoiceGender: (value: VoiceGenderFilter) => void,
  setVoiceAge: (value: VoiceAgeFilter) => void
) => {
  setVoiceAccent('')
  setVoiceGender('any')
  setVoiceAge('any')
}

const openExternalUrl = async (url: string) => {
  if (Platform.OS === 'web' && typeof globalThis.open === 'function') {
    globalThis.open(url, '_self')
    return
  }

  await Linking.openURL(url)
}

const getCheckoutReturnParams = () => {
  if (Platform.OS !== 'web' || typeof globalThis.location === 'undefined') {
    return null
  }

  const params = new URLSearchParams(globalThis.location.search)
  const checkoutState = params.get('checkout')

  if (!checkoutState) {
    return null
  }

  return {
    checkoutState,
    checkoutSessionId: params.get('session_id'),
  }
}

const clearCheckoutReturnParams = () => {
  if (Platform.OS !== 'web' || typeof globalThis.location === 'undefined' || typeof globalThis.history === 'undefined') {
    return
  }

  globalThis.history.replaceState({}, '', globalThis.location.pathname)
}

export const WorkspaceOnboardingExperience = ({ theme }: { theme: Theme }) => {
  const dispatch = useAppDispatch()
  const { width: viewportWidth } = useWindowDimensions()
  const sessionToken = useAppSelector(state => state.auth.sessionToken)
  const workspaceState = useAppSelector(state => state.workspace)
  const authUser = useAppSelector(state => state.auth.user)
  const styles = useMemo(() => createStyles(theme), [theme])

  const [stepIndex, setStepIndex] = useState(0)
  const [planDefinitions, setPlanDefinitions] = useState<PlanDefinition[]>([])
  const [country, setCountry] = useState<CountryCode>(RELAYABLY_DEFAULT_COUNTRY)
  const [selectedPlan, setSelectedPlan] = useState<UsageType>('professional')
  const [hasManualPlanSelection, setHasManualPlanSelection] = useState(false)
  const [numberKind, setNumberKind] = useState<NumberKind>('mobile')
  const [selectedNumber, setSelectedNumber] = useState<RelayablyWorkspaceDocument['workspace']['phoneNumber']>(null)
  const [selectedVoice, setSelectedVoice] = useState<RelayablyVoiceOption | null>(null)
  const [voiceAccent, setVoiceAccent] = useState('')
  const [voiceGender, setVoiceGender] = useState<VoiceGenderFilter>('any')
  const [voiceAge, setVoiceAge] = useState<VoiceAgeFilter>('any')
  const [availableVoiceAccents, setAvailableVoiceAccents] = useState<string[]>([])
  const [voiceCatalog, setVoiceCatalog] = useState<RelayablyVoiceOption[]>([])
  const [voiceLoading, setVoiceLoading] = useState(false)
  const [showVoiceFilters, setShowVoiceFilters] = useState(false)
  const [businessName, setBusinessName] = useState('')
  const [businessWebsite, setBusinessWebsite] = useState('')
  const [businessDescription, setBusinessDescription] = useState('')
  const [interestedCallNotes, setInterestedCallNotes] = useState('')
  const [unsubscribeCallNotes, setUnsubscribeCallNotes] = useState('')
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [calendarProvider, setCalendarProvider] = useState<RelayablyCalendarProvider | null>(null)
  const [calendarApiKey, setCalendarApiKey] = useState('')
  const [calendarTimezone, setCalendarTimezone] = useState<string>('Australia/Sydney')
  const [workingSchedule, setWorkingSchedule] = useState<RelayablyWorkingHoursEntry[]>(defaultWorkingSchedule)
  const [complianceRequirements, setComplianceRequirements] = useState<ComplianceRequirementsState | null>(null)
  const [complianceProfile, setComplianceProfile] = useState<RelayablyPhoneComplianceProfile | null>(null)
  const [complianceLoading, setComplianceLoading] = useState(false)
  const [complianceSaving, setComplianceSaving] = useState(false)
  const [uploadingComplianceRequirement, setUploadingComplianceRequirement] = useState<string | null>(null)
  const [complianceDocumentTypes, setComplianceDocumentTypes] = useState<Record<string, string>>({})
  const [pendingComplianceUploads, setPendingComplianceUploads] = useState<PendingComplianceUploadMap>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [previewLoadingVoiceId, setPreviewLoadingVoiceId] = useState<string | null>(null)
  const [previewPlayingVoiceId, setPreviewPlayingVoiceId] = useState<string | null>(null)
  const [processingCheckoutReturn, setProcessingCheckoutReturn] = useState(() => {
    const checkoutReturn = getCheckoutReturnParams()
    return checkoutReturn?.checkoutState === 'success' && Boolean(checkoutReturn.checkoutSessionId)
  })
  const previewSoundRef = useRef<Audio.Sound | null>(null)
  const processedCheckoutRef = useRef<string | null>(null)

  const workspace = workspaceState.currentWorkspace
  const showCalendarStep = selectedPlan !== 'personal'
  const requiresCompliance = true
  const steps = useMemo(() => getStepDefinitions(selectedPlan, showCalendarStep, requiresCompliance), [selectedPlan, showCalendarStep, requiresCompliance])
  const currentStep = steps[stepIndex]
  const selectedCountryLabel = countryLabelByCode[country]
  const timezoneOptions = timezonesByCountry[country]
  const selectedPlanDefinition = planDefinitions.find(plan => plan.value === selectedPlan) ?? null
  const useVoiceFilterSheet = viewportWidth < 720
  const activeVoiceFilters = useMemo(
    () =>
      getActiveVoiceFilterSummary({
        accent: voiceAccent,
        gender: voiceGender,
        age: voiceAge,
      }),
    [voiceAccent, voiceAge, voiceGender]
  )
  const voiceResults = useMemo(
    () =>
      voiceCatalog.filter(voice =>
        matchesVoiceFilters({
          voice,
          accent: voiceAccent,
          gender: voiceGender,
          age: voiceAge,
        })
      ),
    [voiceAge, voiceAccent, voiceCatalog, voiceGender]
  )
  const calendarEnabled = calendarProvider !== null
  const isBusinessStepLocked = currentStep.key === 'business' && generatingDescription
  const hasValidWebsite = isValidWebsite(businessWebsite)
  const localMissingComplianceFields = useMemo(
    () => getLocalComplianceMissingFields(complianceRequirements, complianceProfile),
    [complianceProfile, complianceRequirements]
  )
  const localMissingComplianceDocuments = useMemo(
    () => getLocalComplianceMissingDocuments(complianceRequirements, complianceProfile, pendingComplianceUploads),
    [complianceProfile, complianceRequirements, pendingComplianceUploads]
  )

  useEffect(() => {
    if (!sessionToken || workspace || workspaceState.status !== 'idle') {
      return
    }

    void dispatch(bootstrapWorkspace())
  }, [dispatch, sessionToken, workspace, workspaceState.status])

  useEffect(() => {
    if (!sessionToken || !workspace?.workspace.phoneNumber || workspace.workspace.phoneNumber.status !== 'pending_review') {
      return
    }

    let cancelled = false

    void relayablyPhoneNumberApi.getCurrentNumber(sessionToken).then(response => {
      if (cancelled || !response.number || response.number.status === workspace.workspace.phoneNumber?.status) {
        return
      }

      dispatch(
        replaceWorkspace({
          ...workspace,
          workspace: {
            ...workspace.workspace,
            phoneNumber: response.number,
          },
        })
      )
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, sessionToken, workspace])

  useEffect(() => {
    if (!sessionToken) {
      return
    }

    let cancelled = false
    setPaymentLoading(true)
    setPaymentError(null)

    void relayablyWorkspaceApi
      .listPaymentProducts(sessionToken)
      .then(response => {
        if (cancelled) {
          return
        }

        const mappedDefinitions = mapPlanDefinitions(response.products)
        setPlanDefinitions(mappedDefinitions)
        if (!mappedDefinitions.find(plan => plan.value === selectedPlan)) {
          setSelectedPlan(mappedDefinitions[0]?.value ?? 'professional')
        }
      })
      .catch(error => {
        if (cancelled) {
          return
        }

        setPlanDefinitions([])
        setPaymentError(error instanceof Error ? error.message : 'Relayably could not load live plan pricing right now.')
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [sessionToken])

  useEffect(() => {
    if (!workspace) {
      return
    }

    const nextCountry =
      countryCodeByLabel[workspace.profile.country] ??
      (workspace.workspace.phoneNumber?.country === RELAYABLY_DEFAULT_COUNTRY ? RELAYABLY_DEFAULT_COUNTRY : undefined) ??
      RELAYABLY_DEFAULT_COUNTRY
    const nextPlan = workspace.workspace.usageType
    const nextCalendar = workspace.workspace.calendarIntegration ?? {
      enabled: false,
      provider: null,
      apiKey: '',
      timezone: '',
      workingHours: {
        days: ['mon', 'tue', 'wed', 'thu', 'fri'] as RelayablyWorkingDay[],
        start: '09:00',
        end: '17:00',
        schedule: defaultWorkingSchedule,
      },
    }
    const nextWorkingHours = nextCalendar.workingHours ?? null

    setCountry(nextCountry)
    if (!hasManualPlanSelection || workspace.onboarding.completed) {
      setSelectedPlan(nextPlan)
    }
    setSelectedNumber(workspace.workspace.phoneNumber)
    setSelectedVoice(workspace.workspace.voiceSelection)
    setBusinessName(workspace.workspace.businessName ?? '')
    setBusinessWebsite(workspace.workspace.website ?? '')
    setBusinessDescription(workspace.workspace.businessDescription ?? '')
    setInterestedCallNotes(workspace.workspace.interestedCallNotes ?? '')
    setUnsubscribeCallNotes(workspace.workspace.unsubscribeCallNotes ?? '')
    setCalendarProvider(nextCalendar.provider)
    setCalendarApiKey(nextCalendar.apiKey)
    setCalendarTimezone(getInitialCalendarTimezone(nextCountry, workspace))
    setWorkingSchedule(buildWorkingSchedule(nextWorkingHours))

    if (workspace.workspace.phoneNumber) {
      const currentCapabilities = workspace.workspace.phoneNumber.capabilities
      if (!currentCapabilities.includes('sms')) {
        setNumberKind('mobile')
      } else if (currentCapabilities.includes('sms') && nextPlan === 'business' && workspace.workspace.phoneNumber.phoneNumber.includes('800')) {
        setNumberKind('tollfree')
      } else {
        setNumberKind('local')
      }
    } else {
      const nextPlanDefinition = planDefinitions.find(plan => plan.value === nextPlan)
      setNumberKind(nextPlanDefinition?.allowedNumberKinds[0] ?? 'mobile')
    }
  }, [hasManualPlanSelection, planDefinitions, workspace])

  useEffect(() => {
    if (!selectedPlanDefinition) {
      return
    }

    if (selectedPlanDefinition.allowedNumberKinds.includes(numberKind)) {
      return
    }

    setNumberKind(selectedPlanDefinition.allowedNumberKinds[0] ?? 'mobile')
  }, [numberKind, selectedPlanDefinition])

  useEffect(() => {
    if (showCalendarStep) {
      return
    }

    setCalendarProvider(null)
    setCalendarApiKey('')
  }, [showCalendarStep])

  useEffect(() => {
    if (timezoneOptions.includes(calendarTimezone)) {
      return
    }

    setCalendarTimezone(getDefaultTimezone(country))
  }, [calendarTimezone, country, timezoneOptions])

  useEffect(() => {
    if (stepIndex < steps.length) {
      return
    }

    setStepIndex(steps.length - 1)
  }, [stepIndex, steps.length])

  useEffect(() => {
    void Audio.setAudioModeAsync({
      staysActiveInBackground: false,
      playsInSilentModeIOS: true,
      interruptionModeIOS: InterruptionModeIOS.DuckOthers,
      interruptionModeAndroid: InterruptionModeAndroid.DuckOthers,
      shouldDuckAndroid: true,
      playThroughEarpieceAndroid: false,
    })
  }, [])

  useEffect(() => {
    return () => {
      const sound = previewSoundRef.current
      previewSoundRef.current = null
      if (sound) {
        void sound.unloadAsync()
      }
    }
  }, [])

  useEffect(() => {
    if (!sessionToken) {
      return
    }

    let active = true

    const loadVoices = async () => {
      setVoiceLoading(true)

      try {
        const response = await relayablyWorkspaceApi.listVoices(sessionToken, {
          pageSize: 500,
        })

        if (!active) {
          return
        }

        setVoiceCatalog(response.voices)
        setAvailableVoiceAccents(response.availableAccents)
      } catch (error) {
        if (!active) {
          return
        }

        setSubmitError(error instanceof Error ? error.message : 'Relayably could not load voices right now.')
      } finally {
        if (!active) {
          return
        }

        setVoiceLoading(false)
      }
    }

    void loadVoices()

    return () => {
      active = false
    }
  }, [sessionToken])

  useEffect(() => {
    if (!sessionToken || !workspace || !authUser) {
      return
    }

    let active = true
    setComplianceLoading(true)

    void (async () => {
      try {
        const response = await relayablyPhoneNumberApi.getComplianceRequirements(sessionToken, {
          country,
          kind: numberKind,
          usageType: selectedPlan,
        })

        if (!active) {
          return
        }

        setComplianceRequirements(response)
        setComplianceProfile(response.profile)
        setComplianceDocumentTypes(previous => {
          const next = { ...previous }
          const assignedTypes = new Set(Object.values(next).filter(Boolean))
          response.requiredDocuments.forEach(requirement => {
            if (!next[requirement.requirementName]) {
              const preferred =
                requirement.acceptedDocuments.find(d => d.type === 'utility_bill' && !assignedTypes.has(d.type)) ??
                requirement.acceptedDocuments.find(d => !assignedTypes.has(d.type)) ??
                requirement.acceptedDocuments[0]
              const type = preferred?.type ?? ''
              next[requirement.requirementName] = type
              if (type) assignedTypes.add(type)
            }
          })
          return next
        })
      } catch (error) {
        if (!active) {
          return
        }

        setComplianceRequirements(null)
        setComplianceProfile(null)
        setSubmitError(error instanceof Error ? error.message : 'Relayably could not load phone compliance requirements.')
      } finally {
        if (active) {
          setComplianceLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [authUser, country, numberKind, selectedPlan, sessionToken, workspace])

  useEffect(() => {
    if (!complianceProfile || !authUser) {
      return
    }

    const { firstName, lastName } = splitName(authUser.fullName)
    setComplianceProfile(current => {
      if (!current) {
        return current
      }

      const next = {
        ...current,
        address: {
          ...current.address,
          friendlyName: current.address.friendlyName || businessName.trim() || workspace?.workspace.workspaceName || authUser.fullName,
          customerName: current.address.customerName || businessName.trim() || authUser.fullName,
          isoCountry: current.address.isoCountry || country,
        },
        endUser: {
          ...current.endUser,
          firstName: current.endUser.firstName || firstName,
          lastName: current.endUser.lastName || lastName,
          email: current.endUser.email || authUser.email,
          phoneNumber: current.endUser.phoneNumber || selectedNumber?.phoneNumber || '',
          comments:
            current.endUser.comments ||
            (selectedPlan === 'personal'
              ? 'Inbound personal call handling through Relayably.'
              : 'Inbound business call handling and routing through Relayably.'),
          businessName: current.endUser.businessName || businessName.trim(),
          businessWebsite: current.endUser.businessWebsite || businessWebsite.trim(),
          websiteUrl: current.endUser.websiteUrl || businessWebsite.trim(),
          businessIdentity: current.endUser.businessIdentity || (selectedPlan === 'personal' ? '' : 'DIRECT_CUSTOMER'),
          businessRegistrationIdentifier:
            current.endUser.businessRegistrationIdentifier ||
            (selectedPlan === 'personal' ? '' : getDefaultBusinessRegistrationIdentifier(country)),
        },
      }

      const changed =
        next.address.friendlyName !== current.address.friendlyName ||
        next.address.customerName !== current.address.customerName ||
        next.address.isoCountry !== current.address.isoCountry ||
        next.endUser.firstName !== current.endUser.firstName ||
        next.endUser.lastName !== current.endUser.lastName ||
        next.endUser.email !== current.endUser.email ||
        next.endUser.phoneNumber !== current.endUser.phoneNumber ||
        next.endUser.comments !== current.endUser.comments ||
        next.endUser.businessName !== current.endUser.businessName ||
        next.endUser.businessWebsite !== current.endUser.businessWebsite ||
        next.endUser.websiteUrl !== current.endUser.websiteUrl ||
        next.endUser.businessIdentity !== current.endUser.businessIdentity ||
        next.endUser.businessRegistrationIdentifier !== current.endUser.businessRegistrationIdentifier

      return changed ? next : current
    })
  }, [authUser, businessName, businessWebsite, complianceProfile, country, selectedNumber?.phoneNumber, selectedPlan, workspace?.workspace.workspaceName])

  useEffect(() => {
    if (!sessionToken || !authUser || workspaceState.status !== 'idle' || !workspace) {
      return
    }

    const checkoutReturn = getCheckoutReturnParams()
    if (!checkoutReturn) {
      return
    }

    const processingKey = `${checkoutReturn.checkoutState}:${checkoutReturn.checkoutSessionId ?? 'none'}`
    if (processedCheckoutRef.current === processingKey) {
      return
    }

    processedCheckoutRef.current = processingKey
    setProcessingCheckoutReturn(checkoutReturn.checkoutState === 'success' && Boolean(checkoutReturn.checkoutSessionId))

    void (async () => {
      const pendingCheckout = await onboardingCheckoutStorage.get()

      if (!pendingCheckout) {
        clearCheckoutReturnParams()
        setProcessingCheckoutReturn(false)
        setSubmitError('Relayably could not find your pending setup draft after checkout.')
        return
      }

      dispatch(replaceWorkspace(pendingCheckout.workspace))
      setPendingComplianceUploads(
        Object.fromEntries((pendingCheckout.pendingComplianceUploads ?? []).map(upload => [upload.requirementName, upload] as const))
      )

      if (checkoutReturn.checkoutState === 'cancelled') {
        clearCheckoutReturnParams()
        setProcessingCheckoutReturn(false)
        setSubmitError('Checkout was cancelled. Your setup draft is still here when you are ready.')
        return
      }

      if (checkoutReturn.checkoutState !== 'success' || !checkoutReturn.checkoutSessionId) {
        clearCheckoutReturnParams()
        setProcessingCheckoutReturn(false)
        setSubmitError('Relayably could not confirm the result of checkout.')
        return
      }

      if (pendingCheckout.checkoutSessionId && pendingCheckout.checkoutSessionId !== checkoutReturn.checkoutSessionId) {
        clearCheckoutReturnParams()
        setProcessingCheckoutReturn(false)
        setSubmitError('Relayably could not match the returned Stripe session to your pending setup.')
        return
      }

      setSubmitting(true)
      setSubmitError(null)

      try {
        const checkoutStatus = await relayablyWorkspaceApi.getCheckoutSession(sessionToken, checkoutReturn.checkoutSessionId)

        if (checkoutStatus.paymentStatus !== 'paid' && checkoutStatus.paymentStatus !== 'no_payment_required') {
          throw new Error('Stripe has not confirmed payment for this checkout yet.')
        }

        const workspaceToSave = cloneWorkspace(pendingCheckout.workspace)
        const resolvedPlan = (checkoutStatus.planId as UsageType | null) ?? pendingCheckout.planId
        applyPlanDefaults(workspaceToSave, resolvedPlan)
        const savedWorkspace = await relayablyWorkspaceApi.completeWorkspace(sessionToken, workspaceToSave)
        const queuedComplianceUploads = pendingCheckout.pendingComplianceUploads ?? []

        for (const upload of queuedComplianceUploads) {
          await relayablyPhoneNumberApi.uploadComplianceDocument(sessionToken, {
            country,
            kind: numberKind,
            usageType: resolvedPlan,
            requirementName: upload.requirementName,
            documentType: upload.documentType,
            fileName: upload.fileName,
            mimeType: upload.mimeType ?? 'application/octet-stream',
            base64: upload.base64,
            label: upload.label,
          })
        }

        if (pendingCheckout.workspace.workspace.phoneNumber && pendingCheckout.workspace.workspace.phoneNumber.status !== 'active') {
          const assignment = await relayablyPhoneNumberApi.assignNumber(sessionToken, pendingCheckout.workspace.workspace.phoneNumber)
          if (assignment.outcome === 'pending_review') {
            setSubmitError(assignment.message)
          }
        }

        const refreshedWorkspace = await relayablyWorkspaceApi.getCurrentWorkspace(sessionToken, {
          email: authUser.email,
          fullName: authUser.fullName,
        })
        const refreshedSession = await relayablyWorkspaceApi.refreshSession(sessionToken)

        if (refreshedWorkspace.workspace.id && refreshedWorkspace.workspace.workspace.phoneNumber?.status === 'active') {
          await phoneForwardingGuideStorage.setPendingWorkspaceId(refreshedWorkspace.workspace.id)
        }

        dispatch(replaceWorkspace(refreshedWorkspace.workspace ?? savedWorkspace.workspace))
        dispatch(applySession(refreshedSession))
        setPendingComplianceUploads({})
        await onboardingCheckoutStorage.clear()
        clearCheckoutReturnParams()
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Relayably could not finish setup after checkout.')
      } finally {
        setProcessingCheckoutReturn(false)
        setSubmitting(false)
      }
    })()
  }, [authUser, dispatch, sessionToken, workspace, workspaceState.status])

  const canMoveNext = () => {
    if (isBusinessStepLocked) {
      return false
    }

    if (currentStep.key === 'plan') {
      return Boolean(selectedPlanDefinition) && !paymentLoading
    }

    if (currentStep.key === 'setup') {
      return Boolean(selectedNumber)
    }

    if (currentStep.key === 'compliance') {
      return Boolean(complianceRequirements) && !complianceLoading && !complianceSaving && localMissingComplianceFields.length === 0 && localMissingComplianceDocuments.length === 0
    }

    if (currentStep.key === 'voice') {
      return Boolean(selectedVoice)
    }

    if (currentStep.key === 'business') {
      return selectedPlan === 'personal' || hasValidWebsite
    }

    if (currentStep.key === 'calendar') {
      if (!calendarEnabled) {
        return true
      }

      return (
        Boolean(calendarApiKey.trim()) &&
        Boolean(calendarTimezone) &&
        workingSchedule.every(entry => entry.closed || (Boolean(entry.start) && Boolean(entry.end)))
      )
    }

    return true
  }

  const handleContinue = async () => {
    if (stepIndex >= steps.length - 1 || !canMoveNext()) {
      return
    }

    if (currentStep.key === 'compliance') {
      try {
        const response = await persistComplianceProfile()
        if (response?.missingFields.length || response?.missingDocuments.length) {
          setSubmitError(
            `Relayably still needs phone compliance details before you can continue. Missing fields: ${response?.missingFields.join(', ') || 'none'}. Missing documents: ${response?.missingDocuments.join(', ') || 'none'}.`
          )
          return
        }
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Relayably could not save your phone compliance details.')
        return
      }
    }

    setStepIndex(index => Math.min(steps.length - 1, index + 1))
  }

  const updateWorkingSchedule = (day: RelayablyWorkingDay, patch: Partial<RelayablyWorkingHoursEntry>) => {
    setWorkingSchedule(previous => previous.map(entry => (entry.day === day ? { ...entry, ...patch } : entry)))
  }

  const handleResetVoiceFilters = () => {
    resetVoiceFilters(setVoiceAccent, setVoiceGender, setVoiceAge)
  }

  const stopVoicePreview = async () => {
    const sound = previewSoundRef.current
    previewSoundRef.current = null
    setPreviewPlayingVoiceId(null)
    setPreviewLoadingVoiceId(null)

    if (!sound) {
      return
    }

    try {
      await sound.stopAsync().catch(() => undefined)
      await sound.unloadAsync()
    } catch {
      // Ignore preview teardown errors so the next sample can still play.
    }
  }

  const handlePlayVoicePreview = async (voice: RelayablyVoiceOption) => {
    if (!voice.previewUrl) {
      return
    }

    if (previewPlayingVoiceId === voice.id) {
      await stopVoicePreview()
      return
    }

    setSubmitError(null)
    setPreviewLoadingVoiceId(voice.id)

    try {
      await stopVoicePreview()
      const { sound } = await Audio.Sound.createAsync(
        { uri: voice.previewUrl },
        { shouldPlay: true, progressUpdateIntervalMillis: 250 }
      )

      previewSoundRef.current = sound
      setPreviewLoadingVoiceId(null)
      setPreviewPlayingVoiceId(voice.id)

      sound.setOnPlaybackStatusUpdate((status: AVPlaybackStatus) => {
        if (!status.isLoaded || !status.didJustFinish) {
          return
        }

        const finishedSound = previewSoundRef.current
        previewSoundRef.current = null
        setPreviewPlayingVoiceId(current => (current === voice.id ? null : current))
        setPreviewLoadingVoiceId(current => (current === voice.id ? null : current))
        if (finishedSound) {
          void finishedSound.unloadAsync()
        }
      })
    } catch (error) {
      setPreviewLoadingVoiceId(null)
      setPreviewPlayingVoiceId(null)
      setSubmitError(error instanceof Error ? error.message : 'Relayably could not play that sample right now.')
    }
  }

  const persistComplianceProfile = async (profileOverride?: RelayablyPhoneComplianceProfile | null) => {
    if (!sessionToken || !complianceProfile) {
      return null
    }

    setComplianceSaving(true)
    try {
      const response = await relayablyPhoneNumberApi.saveComplianceProfile(sessionToken, {
        country,
        kind: numberKind,
        usageType: selectedPlan,
        profile: profileOverride ?? complianceProfile,
      })
      setComplianceProfile(response.profile)
      return response
    } finally {
      setComplianceSaving(false)
    }
  }

  const handleUploadComplianceDocument = async (requirementName: string) => {
    if (!sessionToken || !complianceRequirements || !complianceProfile) {
      return
    }

    const requirement = complianceRequirements.requiredDocuments.find(item => item.requirementName === requirementName)
    if (!requirement) {
      return
    }

    const documentType = complianceDocumentTypes[requirementName] || requirement.acceptedDocuments[0]?.type || ''
    if (!documentType) {
      setSubmitError('Relayably could not determine which document type to upload for this requirement.')
      return
    }

    try {
      setUploadingComplianceRequirement(requirementName)
      const result = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: false,
        type: COMPLIANCE_DOCUMENT_TYPES,
      })

      if (result.canceled) {
        return
      }

      const asset = result.assets[0]
      const base64 = await readUriAsBase64(asset.uri)
      const pendingUpload = {
        requirementName,
        documentType,
        fileName: asset.name,
        mimeType: asset.mimeType ?? 'application/octet-stream',
        base64,
        label: requirement.label,
      } satisfies PendingComplianceUpload

      setPendingComplianceUploads(current => ({
        ...current,
        [requirementName]: pendingUpload,
      }))
      setSubmitError(null)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Relayably could not upload that compliance document.')
    } finally {
      setUploadingComplianceRequirement(null)
    }
  }

  const handleDeleteComplianceDocument = async (documentId: string) => {
    if (pendingComplianceUploads[documentId]) {
      setPendingComplianceUploads(current => {
        const next = { ...current }
        delete next[documentId]
        return next
      })
      setSubmitError(null)
      return
    }

    if (!sessionToken) {
      return
    }

    try {
      const response = await relayablyPhoneNumberApi.deleteComplianceDocument(sessionToken, {
        country,
        kind: numberKind,
        usageType: selectedPlan,
        documentId,
      })
      setComplianceProfile(response.profile)
      setSubmitError(null)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Relayably could not remove that compliance document.')
    }
  }

  const voiceFiltersContent = (
    <>
      <View style={styles.fieldGroup}>
        <Text style={styles.fieldLabel}>Accent</Text>
        <View style={styles.chipGrid}>
          <Pressable
            onPress={() => setVoiceAccent('')}
            style={[styles.filterChip, !voiceAccent ? styles.filterChipActive : undefined]}
          >
            <Text
              style={[
                styles.filterChipText,
                !voiceAccent ? styles.filterChipTextActive : undefined,
              ]}
            >
              Any
            </Text>
          </Pressable>
          {availableVoiceAccents.map(suggestion => (
            <Pressable
              key={suggestion}
              onPress={() => setVoiceAccent(suggestion)}
              style={[styles.filterChip, voiceAccent.toLowerCase() === suggestion.toLowerCase() ? styles.filterChipActive : undefined]}
            >
              <Text
                style={[
                  styles.filterChipText,
                  voiceAccent.toLowerCase() === suggestion.toLowerCase() ? styles.filterChipTextActive : undefined,
                ]}
              >
                {suggestion}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.fieldLabel}>Gender</Text>
        <View style={styles.chipGrid}>
          {voiceGenderFilters.map(filter => (
            <Pressable
              key={filter.value}
              onPress={() => setVoiceGender(filter.value)}
              style={[styles.filterChip, voiceGender === filter.value ? styles.filterChipActive : undefined]}
            >
              <Text style={[styles.filterChipText, voiceGender === filter.value ? styles.filterChipTextActive : undefined]}>{filter.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.fieldLabel}>Voice age</Text>
        <View style={styles.chipGrid}>
          {voiceAgeFilters.map(filter => (
            <Pressable
              key={filter.value}
              onPress={() => setVoiceAge(filter.value)}
              style={[styles.filterChip, voiceAge === filter.value ? styles.filterChipActive : undefined]}
            >
              <Text style={[styles.filterChipText, voiceAge === filter.value ? styles.filterChipTextActive : undefined]}>{filter.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

    </>
  )

  const handleGenerateDescription = async () => {
    if (!sessionToken) {
      return
    }

    if (!hasValidWebsite && businessWebsite.trim()) {
      setSubmitError('Enter a valid website before asking Relayably to draft a description.')
      return
    }

    setGeneratingDescription(true)
    setSubmitError(null)

    try {
      const response = await relayablyWorkspaceApi.describeBusiness(sessionToken, {
        businessName,
        website: businessWebsite,
      })
      setBusinessDescription(response.description)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Relayably could not generate a business description.')
    } finally {
      setGeneratingDescription(false)
    }
  }

  const applyPlanDefaults = (draft: RelayablyWorkspaceDocument, plan: UsageType) => {
    draft.workspace.usageType = plan
    draft.workspace.primaryUseCase = plan === 'personal' ? 'protect_focus_time' : 'capture_sales_enquiries'
    draft.workspace.useCaseFlags = {
      afterHoursAnswering: false,
      bookAppointments: false,
      captureSalesEnquiries: plan !== 'personal',
      handleCustomerSupport: plan !== 'personal',
      protectFocusTime: plan === 'personal',
      qualifyLeads: plan === 'business',
      screenPersonalCalls: true,
    }
  }

  const handleComplete = async () => {
    if (!workspace || !sessionToken || !authUser || !selectedNumber) {
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      if (!selectedPlanDefinition) {
        throw new Error('Relayably could not load your subscription options yet.')
      }

      if (!hasValidWebsite && businessWebsite.trim()) {
        throw new Error('Enter a valid website before finishing setup.')
      }

      let latestComplianceProfile = complianceProfile
      if (complianceProfile) {
        const complianceResponse = await persistComplianceProfile(complianceProfile)
        latestComplianceProfile = complianceResponse?.profile ?? complianceProfile
      }

      const nextWorkspace = buildStructuredWorkspaceDraft({
        authUser,
        businessDescription,
        businessName,
        businessWebsite,
        calendarApiKey,
        calendarEnabled,
        calendarProvider,
        calendarTimezone,
        complianceProfile: latestComplianceProfile,
        country,
        interestedCallNotes,
        selectedNumber,
        selectedPlan,
        selectedVoice,
        unsubscribeCallNotes,
        workingSchedule,
        workspace,
      })

      await onboardingCheckoutStorage.set({
        createdAt: new Date().toISOString(),
        pendingComplianceUploads: Object.values(pendingComplianceUploads),
        planId: selectedPlan,
        workspace: nextWorkspace,
      })

      const checkout = await relayablyWorkspaceApi.createCheckoutSession(sessionToken, selectedPlan, {
        cancelUrl:
          Platform.OS === 'web' && typeof globalThis.location !== 'undefined'
            ? `${globalThis.location.origin}/setup?checkout=cancelled`
            : undefined,
        successUrl:
          Platform.OS === 'web' && typeof globalThis.location !== 'undefined'
            ? `${globalThis.location.origin}/setup?checkout=success&session_id={CHECKOUT_SESSION_ID}`
            : undefined,
      })
      if (!checkout.checkoutUrl) {
        throw new Error('Relayably could not start billing checkout.')
      }

      await onboardingCheckoutStorage.set({
        checkoutSessionId: checkout.checkoutSessionId,
        createdAt: new Date().toISOString(),
        pendingComplianceUploads: Object.values(pendingComplianceUploads),
        planId: selectedPlan,
        workspace: nextWorkspace,
      })

      await openExternalUrl(checkout.checkoutUrl)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Relayably could not finish setup.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogout = () => {
    void dispatch(logout(sessionToken))
  }

  if (!workspace || workspaceState.status === 'loading') {
    return (
      <OnboardingLayout
        accentLabel='Workspace setup'
        atmosphere={{
          glowColors: ['rgba(125, 123, 255, 0.18)', 'rgba(94, 210, 255, 0.08)', 'transparent'],
          railAccent: theme.colors.primary,
          sideBackground: '#05060a',
        }}
        currentStep={1}
        highlights={['Loading your Relayably workspace draft.', 'Preparing the structured onboarding flow.']}
        onLogout={handleLogout}
        subtitle='Getting your setup ready.'
        theme={theme}
        title='Set Up Relayably'
        totalSteps={steps.length}
      >
        <View style={styles.loadingState}>
          <ActivityIndicator color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading your setup…</Text>
        </View>
      </OnboardingLayout>
    )
  }

  if (processingCheckoutReturn) {
    return (
      <OnboardingLayout
        accentLabel='Completing setup'
        atmosphere={{
          glowColors: ['rgba(125, 123, 255, 0.18)', 'rgba(94, 210, 255, 0.08)', 'transparent'],
          railAccent: theme.colors.primary,
          sideBackground: '#05060a',
        }}
        currentStep={steps.length}
        highlights={['Verifying your Stripe checkout.', 'Provisioning your workspace, number, and assistants.']}
        onLogout={handleLogout}
        subtitle='Please wait while Relayably completes your setup.'
        theme={theme}
        title='Processing Checkout'
        totalSteps={steps.length}
      >
        <View style={styles.loadingState}>
          <ActivityIndicator color={theme.colors.primary} />
          <Text style={styles.loadingText}>Processing your checkout…</Text>
        </View>
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      accentLabel={currentStep.accentLabel}
      atmosphere={currentStep.atmosphere}
      currentStep={stepIndex + 1}
      highlights={currentStep.highlights}
      onLogout={handleLogout}
      subtitle={currentStep.subtitle}
      theme={theme}
      title={currentStep.title}
      totalSteps={steps.length}
    >
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {currentStep.key === 'plan' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Plan</Text>
            <Text style={styles.sectionCopy}>Pick the plan that fits you best. This decides what Relayably can help with and which number types you can use.</Text>
            {paymentLoading ? <Text style={styles.helperText}>Loading live pricing…</Text> : null}
            {paymentError ? <Text style={styles.validationText}>{paymentError}</Text> : null}
            {planDefinitions.length ? (
              <View style={styles.grid}>
                {planDefinitions.map(plan => {
                const selected = plan.value === selectedPlan
                const disabled = isRelayablyPlanComingSoon(plan.value)

                return (
                  <Pressable
                    key={plan.value}
                    disabled={disabled}
                    onPress={() => {
                      if (disabled) {
                        return
                      }

                      setSelectedPlan(plan.value)
                      setHasManualPlanSelection(true)
                    }}
                    style={[styles.gridCard, selected ? styles.gridCardActive : undefined, disabled ? styles.gridCardDisabled : undefined]}
                  >
                    <View style={styles.cardHeader}>
                      <View>
                        <View style={styles.planTitleRow}>
                          <Text style={[styles.gridTitle, selected ? styles.gridTitleActive : undefined, disabled ? styles.gridTitleDisabled : undefined]}>
                            {plan.label}
                          </Text>
                          {disabled ? (
                            <View style={styles.comingSoonBadge}>
                              <Text style={styles.comingSoonBadgeText}>Coming Soon</Text>
                            </View>
                          ) : null}
                        </View>
                        <View style={styles.planPriceRow}>
                          <Text style={styles.planPrice}>{plan.displayPrice}</Text>
                          <Text style={styles.planPriceInterval}>{`/${plan.interval}`}</Text>
                        </View>
                      </View>
                      {selected && !disabled ? <Check color={theme.colors.primary} size={18} weight='bold' /> : null}
                    </View>
                    <Text style={styles.gridDescription}>{plan.description}</Text>
                    <View style={styles.featureList}>
                      {plan.features.map(feature => (
                        <Text key={feature} style={styles.featureText}>
                          {`\u2022 ${feature}`}
                        </Text>
                      ))}
                    </View>
                    {disabled ? <Text style={styles.helperText}>Business plan access will open in a later release.</Text> : null}
                    {plan.outboundNote ? <Text style={styles.planNote}>{plan.outboundNote}</Text> : null}
                  </Pressable>
                )
              })}
              </View>
            ) : paymentLoading ? null : (
              <View style={styles.emptyStateCard}>
                <Text style={styles.emptyStateTitle}>Plans unavailable</Text>
                <Text style={styles.emptyStateCopy}>Relayably could not load the live subscription catalog right now.</Text>
              </View>
            )}
          </View>
        ) : null}

        {currentStep.key === 'business' ? (
          <View style={styles.section}>
            {selectedPlan === 'personal' ? (
              <>
                <Text style={styles.sectionTitle}>Call notes</Text>
                <Text style={styles.sectionCopy}>Tell Relayably which calls you care about, and which ones you want stopped quickly.</Text>

                <View style={styles.formCard}>
                  <View style={styles.fieldGroup}>
                    <Text style={styles.fieldLabel}>Calls you want context on</Text>
                    <TextInput
                      autoCorrect
                      multiline
                      onChangeText={setInterestedCallNotes}
                      placeholder='For example: charity calls, recruiters, solar panels, delivery updates.'
                      placeholderTextColor={theme.colors.inactive}
                      style={[styles.textInput, styles.textArea]}
                      textAlignVertical='top'
                      value={interestedCallNotes}
                    />
                  </View>

                  <View style={styles.fieldGroup}>
                    <Text style={styles.fieldLabel}>Calls you want removed</Text>
                    <TextInput
                      autoCorrect
                      multiline
                      onChangeText={setUnsubscribeCallNotes}
                      placeholder='For example: polls, general sales, political outreach, research calls.'
                      placeholderTextColor={theme.colors.inactive}
                      style={[styles.textInput, styles.textArea]}
                      textAlignVertical='top'
                      value={unsubscribeCallNotes}
                    />
                  </View>
                </View>
              </>
            ) : (
              <>
                <Text style={styles.sectionTitle}>Business details</Text>
                <Text style={styles.sectionCopy}>Add a few business details now. You can write the description yourself or let Relayably draft a first version.</Text>

                <View style={styles.formCard}>
                  <View style={styles.fieldGroup}>
                    <Text style={styles.fieldLabel}>Business name</Text>
                    <TextInput
                      autoCorrect={false}
                      editable={!generatingDescription}
                      onChangeText={setBusinessName}
                      placeholder='Enter your business name'
                      placeholderTextColor={theme.colors.inactive}
                      style={styles.textInput}
                      value={businessName}
                    />
                  </View>

                  <View style={styles.fieldGroup}>
                    <Text style={styles.fieldLabel}>Website</Text>
                    <TextInput
                      autoCapitalize='none'
                      autoCorrect={false}
                      editable={!generatingDescription}
                      keyboardType='url'
                      onChangeText={setBusinessWebsite}
                      placeholder='https://example.com'
                      placeholderTextColor={theme.colors.inactive}
                      style={[styles.textInput, !hasValidWebsite && businessWebsite.trim() ? styles.inputError : undefined]}
                      value={businessWebsite}
                    />
                    {!hasValidWebsite && businessWebsite.trim() ? <Text style={styles.validationText}>Enter a valid website, like `example.com` or `example.com/about`.</Text> : null}
                  </View>

                  <View style={styles.fieldGroup}>
                    <View style={styles.inlineHeader}>
                      <Text style={styles.fieldLabel}>Business description</Text>
                      <Pressable
                        disabled={generatingDescription || (!businessName.trim() && !businessWebsite.trim())}
                        onPress={() => void handleGenerateDescription()}
                        style={[
                          styles.inlineAction,
                          generatingDescription || (!businessName.trim() && !businessWebsite.trim()) ? styles.inlineActionDisabled : undefined,
                        ]}
                      >
                        {generatingDescription ? <ActivityIndicator color={theme.colors.primary} size='small' /> : <Text style={styles.inlineActionText}>Draft for me</Text>}
                      </Pressable>
                    </View>
                    <TextInput
                      autoCorrect
                      editable={!generatingDescription}
                      multiline
                      onChangeText={setBusinessDescription}
                      placeholder='Add a short description of what the business does.'
                      placeholderTextColor={theme.colors.inactive}
                      style={[styles.textInput, styles.textArea]}
                      textAlignVertical='top'
                      value={businessDescription}
                    />
                    {generatingDescription ? <Text style={styles.helperText}>Reading the website and writing a draft…</Text> : null}
                  </View>
                </View>
              </>
            )}
          </View>
        ) : null}

        {currentStep.key === 'setup' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Australian number</Text>
            <Text style={styles.sectionCopy}>Choose the Australian number you want to use. If Twilio still needs to approve your compliance, Relayably will hold this number on the approved AU bundle in the meantime.</Text>
            {selectedPlanDefinition ? (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Selected plan</Text>
              <Text style={styles.summaryValue}>{selectedPlanDefinition.label}</Text>
              <Text style={styles.summaryCopy}>This plan includes {selectedPlanDefinition.allowedNumberKinds.map(kind => numberKindLabel[kind]).join(', ')} numbers.</Text>
            </View>
            ) : null}
            <PhoneNumberStep
              allowedKinds={selectedPlanDefinition?.allowedNumberKinds ?? []}
              country={country}
              currentNumber={workspace.workspace.phoneNumber}
              deferAssign
              numberKind={numberKind}
              onCountryChange={setCountry}
              onNumberKindChange={setNumberKind}
              onSelectedChange={setSelectedNumber}
              theme={theme}
              workspaceName={workspace.workspace.workspaceName}
            />
          </View>
        ) : null}

        {currentStep.key === 'compliance' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Phone compliance</Text>
            <Text style={styles.sectionCopy}>Australian numbers can require identity or address proof. Relayably saves your compliance bundle now and will move the number onto it as soon as Twilio approves it.</Text>

            {complianceLoading ? (
              <View style={styles.loadingState}>
                <ActivityIndicator color={theme.colors.primary} />
                <Text style={styles.loadingText}>Loading compliance requirements…</Text>
              </View>
            ) : complianceRequirements && complianceProfile ? (
              <>
                <View style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>Requirement set</Text>
                  <Text style={styles.summaryValue}>{complianceRequirements.regulationLabel || `${selectedCountryLabel} ${numberKindLabel[numberKind]}`}</Text>
                  <Text style={styles.summaryCopy}>{`End user: ${toProperCase(complianceRequirements.endUserType)} • Status: ${toProperCase(complianceProfile.bundleStatus.replace(/_/g, ' '))}`}</Text>
                </View>

                <View style={styles.formCard}>
                  {Array.from(
                    new Set([
                      ...complianceRequirements.fields.map(field => field.key),
                      ...complianceRequirements.requiredDocuments.flatMap(requirement =>
                        requirement.acceptedDocuments.flatMap(document => document.fields)
                      ),
                    ])
                  ).includes('address_sids') ? (
                    <View style={styles.fieldGroup}>
                      <Text style={styles.fieldLabel}>Address details</Text>
                      <View style={styles.complianceFieldGrid}>
                        <TextInput
                          autoCorrect={false}
                          onChangeText={value =>
                            setComplianceProfile(current =>
                              current
                                ? {
                                    ...current,
                                    address: {
                                      ...current.address,
                                      street: value,
                                    },
                                  }
                                : current
                            )
                          }
                          placeholder='Street address'
                          placeholderTextColor={theme.colors.inactive}
                          style={[styles.textInput, styles.complianceTextInput]}
                          value={complianceProfile.address.street}
                        />
                        <TextInput
                          autoCorrect={false}
                          onChangeText={value =>
                            setComplianceProfile(current =>
                              current
                                ? {
                                    ...current,
                                    address: {
                                      ...current.address,
                                      streetSecondary: value,
                                    },
                                  }
                                : current
                            )
                          }
                          placeholder='Address line 2 (optional)'
                          placeholderTextColor={theme.colors.inactive}
                          style={[styles.textInput, styles.complianceTextInput]}
                          value={complianceProfile.address.streetSecondary}
                        />
                        <TextInput
                          autoCorrect={false}
                          onChangeText={value =>
                            setComplianceProfile(current =>
                              current
                                ? {
                                    ...current,
                                    address: {
                                      ...current.address,
                                      city: value,
                                    },
                                  }
                                : current
                            )
                          }
                          placeholder='City'
                          placeholderTextColor={theme.colors.inactive}
                          style={[styles.textInput, styles.complianceTextInput]}
                          value={complianceProfile.address.city}
                        />
                        <TextInput
                          autoCorrect={false}
                          onChangeText={value =>
                            setComplianceProfile(current =>
                              current
                                ? {
                                    ...current,
                                    address: {
                                      ...current.address,
                                      region: value,
                                    },
                                  }
                                : current
                            )
                          }
                          placeholder='State / region'
                          placeholderTextColor={theme.colors.inactive}
                          style={[styles.textInput, styles.complianceTextInput]}
                          value={complianceProfile.address.region}
                        />
                        <TextInput
                          autoCorrect={false}
                          onChangeText={value =>
                            setComplianceProfile(current =>
                              current
                                ? {
                                    ...current,
                                    address: {
                                      ...current.address,
                                      postalCode: value,
                                    },
                                  }
                                : current
                            )
                          }
                          placeholder='Postcode'
                          placeholderTextColor={theme.colors.inactive}
                          style={[styles.textInput, styles.complianceTextInput]}
                          value={complianceProfile.address.postalCode}
                        />
                      </View>
                    </View>
                  ) : null}

                  {Array.from(
                    new Set([
                      ...complianceRequirements.fields.map(field => field.key),
                      ...complianceRequirements.requiredDocuments.flatMap(requirement =>
                        requirement.acceptedDocuments.flatMap(document => document.fields)
                      ),
                    ])
                  )
                    .filter(field => field !== 'address_sids')
                    .map(field => ({
                      key: field,
                      label: toProperCase(field.replace(/_/g, ' ')),
                    }))
                    .map(field =>
                      field.key === 'business_identity' ? (
                        <View key={field.key} style={styles.fieldGroup}>
                          <Text style={styles.fieldLabel}>{field.label}</Text>
                          <View style={styles.chipGrid}>
                            {complianceBusinessIdentityOptions.map(option => (
                              <Pressable
                                key={option.value}
                                onPress={() =>
                                  setComplianceProfile(current =>
                                    current
                                      ? {
                                          ...current,
                                          endUser: {
                                            ...current.endUser,
                                            businessIdentity: option.value,
                                          },
                                        }
                                      : current
                                  )
                                }
                                style={[
                                  styles.filterChip,
                                  complianceProfile.endUser.businessIdentity === option.value ? styles.filterChipActive : undefined,
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.filterChipText,
                                    complianceProfile.endUser.businessIdentity === option.value ? styles.filterChipTextActive : undefined,
                                  ]}
                                >
                                  {option.label}
                                </Text>
                              </Pressable>
                            ))}
                          </View>
                        </View>
                      ) : field.key === 'business_registration_identifier' ? (
                        <View key={field.key} style={styles.fieldGroup}>
                          <Text style={styles.fieldLabel}>{field.label}</Text>
                          <View style={styles.chipGrid}>
                            {complianceRegistrationAuthorityOptions.map(option => (
                              <Pressable
                                key={option.value}
                                onPress={() =>
                                  setComplianceProfile(current =>
                                    current
                                      ? {
                                          ...current,
                                          endUser: {
                                            ...current.endUser,
                                            businessRegistrationIdentifier: option.value,
                                          },
                                        }
                                      : current
                                  )
                                }
                                style={[
                                  styles.filterChip,
                                  complianceProfile.endUser.businessRegistrationIdentifier === option.value
                                    ? styles.filterChipActive
                                    : undefined,
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.filterChipText,
                                    complianceProfile.endUser.businessRegistrationIdentifier === option.value
                                      ? styles.filterChipTextActive
                                      : undefined,
                                  ]}
                                >
                                  {option.label}
                                </Text>
                              </Pressable>
                            ))}
                          </View>
                        </View>
                      ) : field.key === 'is_subassigned' ? (
                        <View key={field.key} style={styles.fieldGroup}>
                          <Text style={styles.fieldLabel}>{field.label}</Text>
                          <View style={styles.chipGrid}>
                            <Pressable
                              onPress={() =>
                                setComplianceProfile(current =>
                                  current
                                    ? {
                                        ...current,
                                        endUser: {
                                          ...current.endUser,
                                          isSubassigned: false,
                                        },
                                      }
                                    : current
                                )
                              }
                              style={[styles.filterChip, !complianceProfile.endUser.isSubassigned ? styles.filterChipActive : undefined]}
                            >
                              <Text style={[styles.filterChipText, !complianceProfile.endUser.isSubassigned ? styles.filterChipTextActive : undefined]}>No</Text>
                            </Pressable>
                            <Pressable
                              onPress={() =>
                                setComplianceProfile(current =>
                                  current
                                    ? {
                                        ...current,
                                        endUser: {
                                          ...current.endUser,
                                          isSubassigned: true,
                                        },
                                      }
                                    : current
                                )
                              }
                              style={[styles.filterChip, complianceProfile.endUser.isSubassigned ? styles.filterChipActive : undefined]}
                            >
                              <Text style={[styles.filterChipText, complianceProfile.endUser.isSubassigned ? styles.filterChipTextActive : undefined]}>Yes</Text>
                            </Pressable>
                          </View>
                        </View>
                      ) : (
                        <View key={field.key} style={styles.fieldGroup}>
                          <Text style={styles.fieldLabel}>{field.label}</Text>
                          <TextInput
                            autoCapitalize={field.key.includes('email') || field.key.includes('website') ? 'none' : 'sentences'}
                            autoCorrect={false}
                            onChangeText={value =>
                              setComplianceProfile(current => (current ? setComplianceFieldValue(current, field.key, value) : current))
                            }
                            placeholder={field.label}
                            placeholderTextColor={theme.colors.inactive}
                            style={[styles.textInput, styles.complianceTextInput]}
                            value={getComplianceFieldValue(complianceProfile, field.key)}
                          />
                        </View>
                      )
                    )}

                  {complianceRequirements.requiredDocuments.length ? (
                    <View style={styles.fieldGroup}>
                      <Text style={styles.fieldLabel}>Supporting evidence</Text>
                      <Text style={styles.helperText}>
                        Select evidence here and Relayably will upload it after you approve the final setup and payment.
                      </Text>
                      <Text style={styles.helperText}>{`Optional file types: ${COMPLIANCE_DOCUMENT_TYPE_LABEL}`}</Text>
                      <View style={styles.complianceDocumentList}>
                        {complianceRequirements.requiredDocuments.map(requirement => {
                          const pendingDocument = pendingComplianceUploads[requirement.requirementName] ?? null
                          const uploadedDocument =
                            complianceProfile.documents.find(document => document.requirementName === requirement.requirementName) ?? null
                          const typesUsedElsewhere = new Set(
                            complianceRequirements.requiredDocuments
                              .filter(r => r.requirementName !== requirement.requirementName)
                              .map(r => complianceDocumentTypes[r.requirementName])
                              .filter(Boolean)
                          )
                          const availableDocuments = requirement.acceptedDocuments.filter(d => !typesUsedElsewhere.has(d.type))

                          return (
                            <View key={requirement.requirementName} style={styles.complianceDocumentCard}>
                              <Text style={styles.complianceDocumentTitle}>{requirement.label}</Text>
                              <View style={styles.chipGrid}>
                                {availableDocuments.map(document => {
                                  const selected = complianceDocumentTypes[requirement.requirementName] === document.type
                                  return (
                                    <Pressable
                                      key={`${requirement.requirementName}-${document.type}`}
                                      onPress={() =>
                                        setComplianceDocumentTypes(current => ({
                                          ...current,
                                          [requirement.requirementName]: document.type,
                                        }))
                                      }
                                      style={[styles.filterChip, selected ? styles.filterChipActive : undefined]}
                                    >
                                      <Text style={[styles.filterChipText, selected ? styles.filterChipTextActive : undefined]}>
                                        {toProperCase(document.type.replace(/_/g, ' '))}
                                      </Text>
                                    </Pressable>
                                  )
                                })}
                              </View>
                              {pendingDocument ? (
                                <View style={styles.uploadedDocumentRow}>
                                  <View style={styles.uploadedDocumentCopy}>
                                    <Text style={styles.uploadedDocumentName}>{pendingDocument.fileName}</Text>
                                    <Text style={styles.helperText}>Ready to upload after payment</Text>
                                  </View>
                                  <Pressable
                                    onPress={() => void handleDeleteComplianceDocument(requirement.requirementName)}
                                    style={styles.inlineAction}
                                  >
                                    <Text style={styles.inlineActionText}>Remove</Text>
                                  </Pressable>
                                </View>
                              ) : null}
                              {uploadedDocument ? (
                                <View style={styles.uploadedDocumentRow}>
                                  <View style={styles.uploadedDocumentCopy}>
                                    <Text style={styles.uploadedDocumentName}>{uploadedDocument.fileName}</Text>
                                    <Text style={styles.helperText}>{toProperCase(uploadedDocument.status || 'uploaded')}</Text>
                                  </View>
                                  <Pressable onPress={() => void handleDeleteComplianceDocument(uploadedDocument.id)} style={styles.inlineAction}>
                                    <Text style={styles.inlineActionText}>Remove</Text>
                                  </Pressable>
                                </View>
                              ) : null}
                              <Pressable
                                disabled={uploadingComplianceRequirement === requirement.requirementName || complianceSaving}
                                onPress={() => void handleUploadComplianceDocument(requirement.requirementName)}
                                style={[
                                  styles.secondaryButton,
                                  uploadingComplianceRequirement === requirement.requirementName || complianceSaving ? styles.secondaryButtonDisabled : undefined,
                                ]}
                              >
                                {uploadingComplianceRequirement === requirement.requirementName ? (
                                  <ActivityIndicator color={theme.colors.primary} size='small' />
                                ) : (
                                  <Text style={styles.secondaryButtonText}>
                                    {pendingDocument || uploadedDocument ? 'Replace evidence file' : 'Choose evidence file'}
                                  </Text>
                                )}
                              </Pressable>
                            </View>
                          )
                        })}
                      </View>
                    </View>
                  ) : null}
                </View>

                {localMissingComplianceFields.length || localMissingComplianceDocuments.length ? (
                  <View style={styles.summaryCard}>
                    <Text style={styles.summaryLabel}>Still needed</Text>
                    {localMissingComplianceFields.map(item => (
                      <Text key={`field-${item}`} style={styles.agentListItem}>{`\u2022 ${item}`}</Text>
                    ))}
                    {localMissingComplianceDocuments.map(item => (
                      <Text key={`doc-${item}`} style={styles.agentListItem}>{`\u2022 ${item} document`}</Text>
                    ))}
                  </View>
                ) : null}
              </>
            ) : (
              <View style={styles.emptyStateCard}>
                <Text style={styles.emptyStateTitle}>No compliance requirements</Text>
                <Text style={styles.emptyStateCopy}>Relayably did not receive any Twilio compliance requirements for this number choice.</Text>
              </View>
            )}
          </View>
        ) : null}

        {currentStep.key === 'voice' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Voice</Text>
            <Text style={styles.sectionCopy}>Choose the voice Relayably should use when it answers. English is locked in for now, and you can filter by accent, gender, and age.</Text>

            <View style={styles.formCard}>
              <View style={styles.voiceToolbar}>
                <View style={styles.voiceLockedChip}>
                  <Text style={styles.voiceLockedChipText}>English</Text>
                </View>
                <Pressable
                  onPress={() => setShowVoiceFilters(current => !current)}
                  style={[styles.voiceLockedChip, showVoiceFilters ? styles.voiceLockedChipActive : undefined]}
                >
                  <Text style={styles.voiceLockedChipText}>{showVoiceFilters ? (useVoiceFilterSheet ? 'Close filters' : 'Hide filters') : 'Filters'}</Text>
                </Pressable>
              </View>

              {activeVoiceFilters.length ? (
                <View style={styles.voiceSummaryWrap}>
                  {activeVoiceFilters.map(filter => (
                    <View key={filter} style={styles.voiceSummaryChip}>
                      <Text style={styles.voiceSummaryChipText}>{filter}</Text>
                    </View>
                  ))}
                </View>
              ) : null}

              {showVoiceFilters && !useVoiceFilterSheet ? (
                <View style={styles.compactFiltersCard}>
                  {voiceFiltersContent}
                </View>
              ) : null}

              {voiceLoading ? (
                <View style={styles.loadingState}>
                  <ActivityIndicator color={theme.colors.primary} />
                  <Text style={styles.loadingText}>Loading voices…</Text>
                </View>
              ) : (
                <View style={styles.voiceResultsGrid}>
                  {voiceResults.map(voice => {
                    const selected = selectedVoice?.id === voice.id
                    return (
                      <Pressable key={voice.id} onPress={() => setSelectedVoice(voice)} style={[styles.voiceCard, selected ? styles.voiceCardActive : undefined]}>
                        <View style={styles.cardHeader}>
                          <View style={styles.voiceHeaderContent}>
                            <Text style={[styles.gridTitle, selected ? styles.gridTitleActive : undefined]}>{voice.name}</Text>
                            <Text style={styles.voiceMetaText}>
                              {[voice.accent, voice.gender, voiceAgeFilters.find(filter => filter.value === voice.age)?.label ?? '']
                                .filter(Boolean)
                                .map(toProperCase)
                                .join(' • ')}
                            </Text>
                          </View>
                          {selected ? <Check color={theme.colors.primary} size={18} weight='bold' /> : null}
                        </View>
                        <View style={styles.voiceBadgeRow}>
                          {[voice.accent, voice.gender, voiceAgeFilters.find(filter => filter.value === voice.age)?.label ?? '']
                            .filter(Boolean)
                            .map(tag => (
                              <View key={`${voice.id}-${tag}`} style={styles.voiceBadge}>
                                <Text style={styles.voiceBadgeText}>{toProperCase(tag)}</Text>
                              </View>
                            ))}
                        </View>
                        {voice.previewUrl ? (
                          <Pressable
                            onPress={() => void handlePlayVoicePreview(voice)}
                            style={[
                              styles.voicePreviewAction,
                              previewPlayingVoiceId === voice.id ? styles.voicePreviewActionActive : undefined,
                              previewLoadingVoiceId === voice.id ? styles.voicePreviewActionDisabled : undefined,
                            ]}
                          >
                            {previewLoadingVoiceId === voice.id ? (
                              <ActivityIndicator color={theme.colors.primary} size='small' />
                            ) : (
                              <Text
                                style={[
                                  styles.voicePreviewActionText,
                                  previewPlayingVoiceId === voice.id ? styles.voicePreviewActionTextActive : undefined,
                                ]}
                              >
                                {previewPlayingVoiceId === voice.id ? 'Stop sample' : 'Play sample'}
                              </Text>
                            )}
                          </Pressable>
                        ) : null}
                      </Pressable>
                    )
                  })}

                  {!voiceResults.length ? (
                    <View style={styles.emptyStateCard}>
                      <Text style={styles.emptyStateTitle}>No voices found</Text>
                      <Text style={styles.emptyStateCopy}>Try a different accent or clear the filters to see more English voices.</Text>
                      <Pressable
                        onPress={() =>
                          resetVoiceFilters(
                            setVoiceAccent,
                            setVoiceGender,
                            setVoiceAge
                          )
                        }
                        style={styles.emptyStateAction}
                      >
                        <Text style={styles.emptyStateActionText}>Reset search</Text>
                      </Pressable>
                    </View>
                  ) : null}
                </View>
              )}

            </View>
          </View>
        ) : null}

        {currentStep.key === 'voice' && useVoiceFilterSheet ? (
          <Modal animationType='slide' onRequestClose={() => setShowVoiceFilters(false)} transparent visible={showVoiceFilters}>
            <Pressable onPress={() => setShowVoiceFilters(false)} style={styles.sheetOverlay} />
            <View style={styles.sheetWrap}>
              <View style={styles.sheetCard}>
                <View style={styles.sheetHeader}>
                  <Text style={styles.sheetTitle}>Voice filters</Text>
                  <View style={styles.sheetActions}>
                    <Pressable onPress={handleResetVoiceFilters} style={styles.sheetTextAction}>
                      <Text style={styles.sheetTextActionLabel}>Reset</Text>
                    </Pressable>
                    <Pressable onPress={() => setShowVoiceFilters(false)} style={styles.sheetDoneAction}>
                      <Text style={styles.sheetDoneActionLabel}>Done</Text>
                    </Pressable>
                  </View>
                </View>
                <ScrollView contentContainerStyle={styles.sheetContent} showsVerticalScrollIndicator={false}>
                  {voiceFiltersContent}
                </ScrollView>
              </View>
            </View>
          </Modal>
        ) : null}

        {currentStep.key === 'calendar' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Calendar integration</Text>
            <Text style={styles.sectionCopy}>This step is optional. If you want calls to turn into bookings, connect Calendly or Cal.com and add your schedule.</Text>

            <View style={styles.grid}>
              <Pressable onPress={() => setCalendarProvider(null)} style={[styles.gridCard, !calendarEnabled ? styles.gridCardActive : undefined]}>
                <Text style={[styles.gridTitle, !calendarEnabled ? styles.gridTitleActive : undefined]}>No calendar for now</Text>
                <Text style={styles.gridDescription}>Skip this for now. You can add it later.</Text>
              </Pressable>

              {(['calendly', 'cal.com'] as RelayablyCalendarProvider[]).map(provider => {
                const selected = calendarProvider === provider

                return (
                  <Pressable key={provider} onPress={() => setCalendarProvider(provider)} style={[styles.gridCard, selected ? styles.gridCardActive : undefined]}>
                    <Text style={[styles.gridTitle, selected ? styles.gridTitleActive : undefined]}>{providerLabel[provider]}</Text>
                    <Text style={styles.gridDescription}>Add your API key and working hours so Relayably can help book calls into your schedule.</Text>
                  </Pressable>
                )
              })}
            </View>

            {calendarEnabled ? (
              <View style={styles.formCard}>
                <View style={styles.fieldGroup}>
                  <Text style={styles.fieldLabel}>API Key</Text>
                  <TextInput
                    autoCapitalize='none'
                    autoCorrect={false}
                    onChangeText={setCalendarApiKey}
                    placeholder={`Enter your ${calendarProvider ? providerLabel[calendarProvider] : 'calendar'} API key`}
                    placeholderTextColor={theme.colors.inactive}
                    style={styles.textInput}
                    value={calendarApiKey}
                  />
                </View>

                <View style={styles.fieldGroup}>
                  <Text style={styles.fieldLabel}>Timezone</Text>
                  <View style={styles.grid}>
                    {timezoneOptions.map(option => (
                      <Pressable
                        key={option}
                        onPress={() => setCalendarTimezone(option)}
                        style={[styles.gridCardCompact, calendarTimezone === option ? styles.gridCardActive : undefined]}
                      >
                        <Text style={[styles.gridTitleCompact, calendarTimezone === option ? styles.gridTitleActive : undefined]}>{option}</Text>
                      </Pressable>
                    ))}
                  </View>
                </View>

                <View style={styles.fieldGroup}>
                  <Text style={styles.fieldLabel}>Preferred working hours</Text>
                  <View style={styles.scheduleTable}>
                    <View style={styles.scheduleHeaderRow}>
                      <Text style={[styles.scheduleHeaderCell, styles.scheduleDayCell]}>Day</Text>
                      <Text style={styles.scheduleHeaderCell}>Start</Text>
                      <Text style={styles.scheduleHeaderCell}>Finish</Text>
                    </View>

                    {workingDayOptions.map(dayOption => {
                      const entry = workingSchedule.find(item => item.day === dayOption.value) ?? {
                        day: dayOption.value,
                        closed: dayOption.value === 'sat' || dayOption.value === 'sun',
                        start: '09:00',
                        end: '17:00',
                      }

                      return (
                        <View key={dayOption.value} style={styles.scheduleRow}>
                          <Text style={[styles.scheduleDayText, styles.scheduleDayCell]}>{dayOption.label}</Text>
                          <Pressable
                            onPress={() => updateWorkingSchedule(dayOption.value, { closed: !entry.closed })}
                            style={[styles.closedChip, entry.closed ? styles.closedChipActive : undefined]}
                          >
                            <Text style={[styles.closedChipText, entry.closed ? styles.closedChipTextActive : undefined]}>{entry.closed ? 'Closed' : 'Open'}</Text>
                          </Pressable>
                          <View style={styles.scheduleTimeColumn}>
                            <SelectDropdown
                              data={timeOptions}
                              defaultValue={entry.start}
                              disabled={entry.closed}
                              buttonStyle={[styles.scheduleDropdownButton, entry.closed ? styles.scheduleDropdownButtonDisabled : undefined]}
                              buttonTextStyle={[styles.scheduleDropdownButtonText, entry.closed ? styles.scheduleDropdownButtonTextDisabled : undefined]}
                              dropdownStyle={styles.scheduleDropdownMenu}
                              rowStyle={styles.scheduleDropdownRow}
                              rowTextStyle={styles.scheduleDropdownRowText}
                              onSelect={selectedItem => updateWorkingSchedule(dayOption.value, { start: selectedItem })}
                              buttonTextAfterSelection={selectedItem => selectedItem}
                              rowTextForSelection={item => item}
                            />
                          </View>
                          <View style={styles.scheduleTimeColumn}>
                            <SelectDropdown
                              data={timeOptions}
                              defaultValue={entry.end}
                              disabled={entry.closed}
                              buttonStyle={[styles.scheduleDropdownButton, entry.closed ? styles.scheduleDropdownButtonDisabled : undefined]}
                              buttonTextStyle={[styles.scheduleDropdownButtonText, entry.closed ? styles.scheduleDropdownButtonTextDisabled : undefined]}
                              dropdownStyle={styles.scheduleDropdownMenu}
                              rowStyle={styles.scheduleDropdownRow}
                              rowTextStyle={styles.scheduleDropdownRowText}
                              onSelect={selectedItem => updateWorkingSchedule(dayOption.value, { end: selectedItem })}
                              buttonTextAfterSelection={selectedItem => selectedItem}
                              rowTextForSelection={item => item}
                            />
                          </View>
                        </View>
                      )
                    })}
                  </View>
                </View>
              </View>
            ) : null}
          </View>
        ) : null}

        {currentStep.key === 'done' ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Done</Text>
            <Text style={styles.sectionCopy}>When you finish, Relayably will open billing first. After payment is confirmed, Relayably will save your setup, provision the right assistants, and attach your selected number.</Text>

            {selectedPlanDefinition ? (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Plan</Text>
              <Text style={styles.summaryValue}>{selectedPlanDefinition.label}</Text>
              <Text style={styles.summaryCopy}>{`${selectedPlanDefinition.displayPrice} / ${selectedPlanDefinition.interval}`}</Text>
            </View>
            ) : null}

            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Country</Text>
              <Text style={styles.summaryValue}>{selectedCountryLabel}</Text>
              <Text style={styles.summaryCopy}>Your number will be saved in this country.</Text>
            </View>

            {selectedPlan === 'personal' ? (
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Call notes</Text>
                <Text style={styles.summaryValue}>{interestedCallNotes.trim() || 'No extra notes yet'}</Text>
                <Text style={styles.summaryCopy}>{unsubscribeCallNotes.trim() || 'You can add these notes later if you want to.'}</Text>
              </View>
            ) : (
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Business details</Text>
                <Text style={styles.summaryValue}>{businessName.trim() || 'Not added yet'}</Text>
                <Text style={styles.summaryCopy}>{businessDescription.trim() || businessWebsite.trim() || 'You can add this later if you want to.'}</Text>
              </View>
            )}

            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Reserved number</Text>
              <Text style={styles.summaryValue}>{selectedNumber?.phoneNumber ?? 'No number selected yet'}</Text>
              <Text style={styles.summaryCopy}>{selectedNumber ? `${numberKindLabel[numberKind]} number saved. We have not bought it yet.` : 'Choose a number in the previous step.'}</Text>
            </View>

            {requiresCompliance && complianceProfile ? (
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>Phone compliance</Text>
                <Text style={styles.summaryValue}>{toProperCase(complianceProfile.bundleStatus.replace(/_/g, ' '))}</Text>
                <Text style={styles.summaryCopy}>
                  {localMissingComplianceFields.length || localMissingComplianceDocuments.length
                    ? `Still needed: ${[...localMissingComplianceFields, ...localMissingComplianceDocuments.map(item => `${item} document`)].join(', ')}`
                    : complianceProfile.bundleStatus === 'pending_review' || complianceProfile.bundleStatus === 'in_review'
                      ? 'Relayably has submitted your Twilio bundle. We are waiting for approval before the number can be purchased.'
                      : 'The required phone compliance details are ready for provisioning.'}
                </Text>
              </View>
            ) : null}

            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Voice</Text>
              <Text style={styles.summaryValue}>{selectedVoice?.name ?? 'No voice selected yet'}</Text>
              <Text style={styles.summaryCopy}>
                {selectedVoice
                  ? [selectedVoice.accent, selectedVoice.gender, voiceAgeFilters.find(filter => filter.value === selectedVoice.age)?.label ?? ''].filter(Boolean).join(' • ')
                  : 'Choose a voice in the previous step.'}
              </Text>
            </View>

            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Calendar</Text>
              <Text style={styles.summaryValue}>{calendarEnabled && calendarProvider ? providerLabel[calendarProvider] : 'Not connected yet'}</Text>
              <Text style={styles.summaryCopy}>
                {calendarEnabled
                  ? `${calendarTimezone} • ${workingSchedule
                      .map(entry => `${entry.day} ${entry.closed ? 'closed' : `${entry.start}-${entry.end}`}`)
                      .slice(0, 3)
                      .join(' • ')}${workingSchedule.length > 3 ? ' …' : ''}`
                  : 'You can add this later if you want to.'}
              </Text>
            </View>

            {selectedPlanDefinition ? (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Assistants</Text>
              {selectedPlanDefinition.keyAgents.map(agent => (
                <Text key={agent} style={styles.agentListItem}>
                  {`\u2022 ${agent}`}
                </Text>
              ))}
            </View>
            ) : null}
          </View>
        ) : null}

        {submitError ? (
          <View style={styles.errorCard}>
            <Text style={styles.errorText}>{submitError}</Text>
          </View>
        ) : null}

        <View style={styles.footerActions}>
          <Pressable
            disabled={stepIndex === 0 || submitting || isBusinessStepLocked}
            onPress={() => setStepIndex(index => Math.max(0, index - 1))}
            style={[styles.secondaryButton, stepIndex === 0 || isBusinessStepLocked ? styles.secondaryButtonDisabled : undefined]}
          >
            <Text style={[styles.secondaryButtonText, stepIndex === 0 || isBusinessStepLocked ? styles.secondaryButtonTextDisabled : undefined]}>Back</Text>
          </Pressable>

          {stepIndex < steps.length - 1 ? (
            <Pressable
              disabled={!canMoveNext()}
              onPress={() => void handleContinue()}
              style={[styles.primaryButton, !canMoveNext() ? styles.primaryButtonDisabled : undefined]}
            >
              <Text style={styles.primaryButtonText}>Next</Text>
            </Pressable>
          ) : (
            <Pressable
              disabled={submitting || !selectedNumber || !selectedPlanDefinition || isRelayablyPlanComingSoon(selectedPlan)}
              onPress={() => void handleComplete()}
              style={[
                styles.primaryButton,
                submitting || !selectedNumber || !selectedPlanDefinition || isRelayablyPlanComingSoon(selectedPlan)
                  ? styles.primaryButtonDisabled
                  : undefined,
              ]}
            >
              {submitting ? <ActivityIndicator color={theme.colors.staticWhite} /> : <Text style={styles.primaryButtonText}>Continue to billing</Text>}
            </Pressable>
          )}
        </View>
      </ScrollView>
    </OnboardingLayout>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    content: {
      gap: 24,
      paddingBottom: 40,
    },
    loadingState: {
      alignItems: 'center',
      gap: 12,
      justifyContent: 'center',
      minHeight: 280,
    },
    loadingText: {
      color: theme.colors.inactive,
      fontSize: 14,
    },
    section: {
      gap: 16,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 28,
      lineHeight: 34,
    },
    sectionCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    fieldGroup: {
      gap: 12,
    },
    fieldLabel: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.2,
      textTransform: 'uppercase',
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    voiceToolbar: {
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    voiceLockedChip: {
      alignItems: 'center',
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: 'rgba(125,123,255,0.16)',
      borderRadius: 999,
      borderWidth: 1,
      justifyContent: 'center',
      minHeight: 56,
      minWidth: 120,
      paddingHorizontal: 18,
    },
    voiceLockedChipActive: {
      backgroundColor: 'rgba(125,123,255,0.14)',
      borderColor: theme.colors.primary,
    },
    voiceLockedChipText: {
      color: theme.colors.primary,
      fontSize: 14,
      fontWeight: '700',
    },
    voiceSummaryWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    voiceSummaryChip: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.04)' : '#f3f4f8',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    voiceSummaryChipText: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '600',
    },
    compactFiltersCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.02)' : '#f8f8fc',
      borderColor: 'rgba(125,123,255,0.08)',
      borderRadius: 16,
      borderWidth: 1,
      gap: 16,
      padding: 16,
    },
    sheetOverlay: {
      backgroundColor: 'rgba(5, 7, 16, 0.42)',
      flex: 1,
    },
    sheetWrap: {
      bottom: 0,
      left: 0,
      position: 'absolute',
      right: 0,
    },
    sheetCard: {
      backgroundColor: theme.colors.background,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      maxHeight: '80%',
      overflow: 'hidden',
      paddingBottom: 24,
      paddingHorizontal: 18,
      paddingTop: 18,
    },
    sheetHeader: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 14,
    },
    sheetTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
    },
    sheetActions: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 10,
    },
    sheetTextAction: {
      paddingHorizontal: 8,
      paddingVertical: 6,
    },
    sheetTextActionLabel: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '700',
    },
    sheetDoneAction: {
      backgroundColor: 'rgba(125,123,255,0.12)',
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    sheetDoneActionLabel: {
      color: theme.colors.primary,
      fontSize: 13,
      fontWeight: '700',
    },
    sheetContent: {
      gap: 16,
      paddingBottom: 8,
    },
    chipGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    filterChip: {
      alignItems: 'center',
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 999,
      borderWidth: 1,
      justifyContent: 'center',
      minHeight: 42,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    filterChipActive: {
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: theme.colors.primary,
    },
    filterChipText: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '700',
    },
    filterChipTextActive: {
      color: theme.colors.primary,
    },
    gridCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 18,
      borderWidth: 1,
      flexGrow: 1,
      gap: 10,
      minWidth: 220,
      padding: 16,
      width: '31%',
    },
    gridCardCompact: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 16,
      borderWidth: 1,
      flexGrow: 1,
      minWidth: 140,
      padding: 14,
      width: '31%',
    },
    gridCardActive: {
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: theme.colors.primary,
    },
    gridCardDisabled: {
      opacity: 0.56,
    },
    cardHeader: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'space-between',
    },
    planTitleRow: {
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    gridTitle: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    gridTitleCompact: {
      color: theme.colors.text,
      fontSize: 15,
      fontWeight: '700',
    },
    gridTitleActive: {
      color: theme.colors.primary,
    },
    gridTitleDisabled: {
      color: theme.colors.inactive,
    },
    comingSoonBadge: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.06)' : '#ececf5',
      borderColor: 'rgba(125,123,255,0.14)',
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 8,
      paddingVertical: 4,
    },
    comingSoonBadgeText: {
      color: theme.colors.inactive,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    gridDescription: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    planPrice: {
      color: theme.colors.text,
      fontSize: 24,
      fontWeight: '700',
      marginTop: 6,
    },
    planPriceRow: {
      alignItems: 'flex-end',
      flexDirection: 'row',
      gap: 4,
      marginTop: 6,
    },
    planPriceInterval: {
      color: theme.colors.inactive,
      fontSize: 14,
      fontWeight: '600',
      marginBottom: 2,
    },
    featureList: {
      gap: 6,
      marginTop: 4,
    },
    featureText: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
    },
    planNote: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '600',
      lineHeight: 18,
      marginTop: 6,
    },
    summaryCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 18,
      borderWidth: 1,
      gap: 8,
      padding: 16,
    },
    summaryLabel: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.1,
      textTransform: 'uppercase',
    },
    summaryValue: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
    },
    summaryCopy: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    formCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.02)' : '#fbfbfe',
      borderColor: 'rgba(125,123,255,0.08)',
      borderRadius: 18,
      borderWidth: 1,
      gap: 18,
      padding: 18,
    },
    complianceFieldGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    complianceDocumentList: {
      gap: 12,
    },
    complianceDocumentCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      borderColor: 'rgba(125,123,255,0.08)',
      borderRadius: 16,
      borderWidth: 1,
      gap: 12,
      padding: 14,
    },
    complianceDocumentTitle: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '700',
    },
    uploadedDocumentRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'space-between',
    },
    uploadedDocumentCopy: {
      flex: 1,
      gap: 4,
    },
    uploadedDocumentName: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '600',
    },
    inlineHeader: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
    },
    inlineAction: {
      alignItems: 'center',
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.08)' : 'rgba(125,123,255,0.08)',
      borderColor: 'rgba(125,123,255,0.2)',
      borderRadius: 999,
      borderWidth: 1,
      justifyContent: 'center',
      minHeight: 36,
      minWidth: 124,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    inlineActionDisabled: {
      opacity: 0.55,
    },
    inlineActionText: {
      color: theme.colors.primary,
      fontSize: 13,
      fontWeight: '700',
    },
    textInput: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      borderColor: 'rgba(125,123,255,0.12)',
      borderRadius: 16,
      borderWidth: 1,
      color: theme.colors.text,
      minHeight: 56,
      paddingHorizontal: 16,
      paddingVertical: 14,
    },
    complianceTextInput: {
      minWidth: 220,
      width: '48%',
    },
    inputError: {
      borderColor: '#ffb780',
    },
    textArea: {
      minHeight: 140,
    },
    helperText: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
    },
    voiceResultsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    voiceCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 18,
      borderWidth: 1,
      flexGrow: 1,
      gap: 10,
      minWidth: 240,
      padding: 16,
      width: '31%',
    },
    voiceCardActive: {
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: theme.colors.primary,
    },
    voiceHeaderContent: {
      flex: 1,
      gap: 6,
    },
    voiceMetaText: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
      textTransform: 'capitalize',
    },
    voiceBadgeRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    voiceBadge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      borderWidth: 1,
      borderColor: 'rgba(125,123,255,0.12)',
    },
    voiceBadgeText: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'capitalize',
    },
    voicePreviewAction: {
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.12)' : 'rgba(125,123,255,0.08)',
      borderRadius: 999,
      justifyContent: 'center',
      marginTop: 12,
      minHeight: 36,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    voicePreviewActionActive: {
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.24)' : 'rgba(125,123,255,0.16)',
    },
    voicePreviewActionDisabled: {
      opacity: 0.72,
    },
    voicePreviewActionText: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    voicePreviewActionTextActive: {
      color: theme.colors.text,
    },
    emptyStateCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.02)' : '#fbfbfe',
      borderColor: 'rgba(125,123,255,0.08)',
      borderRadius: 18,
      borderWidth: 1,
      gap: 8,
      padding: 18,
      width: '100%',
    },
    emptyStateTitle: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    emptyStateCopy: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    emptyStateAction: {
      alignSelf: 'flex-start',
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.14)' : 'rgba(125,123,255,0.1)',
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 9,
    },
    emptyStateActionText: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    validationText: {
      color: '#ffb780',
      fontSize: 12,
      lineHeight: 18,
    },
    scheduleTable: {
      borderColor: 'rgba(125,123,255,0.08)',
      borderRadius: 18,
      borderWidth: 1,
      overflow: 'hidden',
    },
    scheduleHeaderRow: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f6f6fb',
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 14,
      paddingVertical: 12,
    },
    scheduleHeaderCell: {
      color: theme.colors.inactive,
      flex: 1,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.1,
      textTransform: 'uppercase',
    },
    scheduleRow: {
      alignItems: 'center',
      borderTopColor: 'rgba(125,123,255,0.08)',
      borderTopWidth: 1,
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 14,
      paddingVertical: 12,
    },
    scheduleDayCell: {
      flex: 0.7,
    },
    scheduleDayText: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '600',
    },
    closedChip: {
      alignItems: 'center',
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f6f6fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 999,
      borderWidth: 1,
      justifyContent: 'center',
      minWidth: 74,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    closedChipActive: {
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: theme.colors.primary,
    },
    closedChipText: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
    },
    closedChipTextActive: {
      color: theme.colors.primary,
    },
    scheduleTimeColumn: {
      flex: 1,
    },
    scheduleTimeGrid: {
      width: '100%',
    },
    scheduleDropdownButton: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      borderColor: 'rgba(125,123,255,0.12)',
      borderRadius: 12,
      borderWidth: 1,
      height: 44,
      paddingHorizontal: 12,
      width: '100%',
    },
    scheduleDropdownButtonDisabled: {
      opacity: 0.45,
    },
    scheduleDropdownButtonText: {
      color: theme.colors.text,
      fontSize: 14,
      marginHorizontal: 0,
      textAlign: 'left',
    },
    scheduleDropdownButtonTextDisabled: {
      color: theme.colors.inactive,
    },
    scheduleDropdownMenu: {
      backgroundColor: theme.colors.card,
      borderColor: 'rgba(125,123,255,0.12)',
      borderRadius: 12,
      borderWidth: 1,
      maxHeight: 240,
    },
    scheduleDropdownRow: {
      backgroundColor: theme.colors.card,
      borderBottomColor: 'rgba(125,123,255,0.08)',
      borderBottomWidth: 1,
      height: 40,
    },
    scheduleDropdownRowText: {
      color: theme.colors.text,
      fontSize: 13,
    },
    agentListItem: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    errorCard: {
      backgroundColor: 'rgba(255, 140, 80, 0.08)',
      borderColor: 'rgba(255, 140, 80, 0.28)',
      borderRadius: 18,
      borderWidth: 1,
      padding: 16,
    },
    errorText: {
      color: '#ffb780',
      fontSize: 14,
      lineHeight: 22,
    },
    footerActions: {
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'space-between',
      marginTop: 8,
    },
    primaryButton: {
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 18,
      flex: 1,
      justifyContent: 'center',
      minHeight: 58,
      paddingHorizontal: 24,
    },
    primaryButtonDisabled: {
      opacity: 0.65,
    },
    primaryButtonText: {
      color: theme.colors.staticWhite,
      fontSize: 16,
      fontWeight: '700',
    },
    secondaryButton: {
      alignItems: 'center',
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f6f6fb',
      borderColor: theme.colors.border,
      borderRadius: 18,
      borderWidth: 1,
      flex: 1,
      justifyContent: 'center',
      minHeight: 58,
      paddingHorizontal: 24,
    },
    secondaryButtonDisabled: {
      opacity: 0.45,
    },
    secondaryButtonText: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    secondaryButtonTextDisabled: {
      color: theme.colors.inactive,
    },
  })
